/**
 * Elite Batch 7 Part 1 — Workflow Templates 548–567
 * Upsell, VIP, Warranty, WhatsApp, Wholesale, Wishlist, WooCommerce, eBay
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function pushNode(
  id: string,
  label: string,
  y: number,
  title: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "pushNotification",
    label,
    y,
    { pushNotificationDetails: { title, body } },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function salesforceNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{sf_instance}.salesforce.com/services/data/v59.0/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {salesforce_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function zendeskNode(
  id: string,
  label: string,
  y: number,
  subject: string,
  body: string,
  priority: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Bearer {zendesk_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value: `{"subject":"${subject}","comment":{"body":"${body}"},"priority":"${priority}"}`,
          },
        ],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 7 PART 1 — TEMPLATES 548–567
// =============================================================================

export const ELITE_BATCH7_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 548. UPSELL AFTER FIRST PURCHASE (8 nodes)
  // ===================================================================

  "Upsell After First Purchase": {
    summary:
      "Cross-sells higher-margin products immediately after a first order. A Shopify order webhook triggers product and margin data pull, AI scores upsell opportunities, routes on preferred channel, sends a personalized upsell email or SMS alternative, writes the upsell attempt to HubSpot CRM, and checks conversion after 48 hours with logging to Sheets.",
    nodes: [
      trigger("Webhook: Shopify first order placed", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts:
          "A Shopify order is created for a first-time buyer, triggering an upsell opportunity within the post-purchase window",
        setup:
          "Configure Shopify webhook for orders/create. Set up product margin data in Shopify metafields or a lookup sheet. Provide HubSpot and OpenAI API keys.",
        inputFields: [],
      }),

      // 2. Pull product + margin data from Shopify
      shopifyNode(
        "2",
        "Pull order product and margin data (Shopify)",
        280,
        "GET",
        "orders/{order_id}.json?fields=id,name,email,line_items,total_price,customer",
        [],
        [
          { name: "order.email", customName: "customer_email", type: "string" },
          {
            name: "order.customer.first_name",
            customName: "customer_name",
            type: "string",
          },
          {
            name: "order.customer.phone",
            customName: "customer_phone",
            type: "string",
          },
          {
            name: "order.line_items[0].title",
            customName: "purchased_product",
            type: "string",
          },
          {
            name: "order.line_items[0].product_id",
            customName: "purchased_product_id",
            type: "string",
          },
          {
            name: "order.line_items[0].price",
            customName: "purchased_price",
            type: "string",
          },
          {
            name: "order.total_price",
            customName: "order_total",
            type: "string",
          },
          { name: "order.name", customName: "order_name", type: "string" },
          {
            name: "order.customer.orders_count",
            customName: "orders_count",
            type: "number",
          },
        ],
      ),

      // 3. AI upsell scoring
      aiNode(
        "3",
        "AI upsell opportunity scoring",
        520,
        "You are an e-commerce upsell strategist. Given a customer's first purchase, identify the best upsell product, calculate an upsell score, and write a compelling pitch. Consider product complementarity, price anchoring (upsell should be 20-50% of original), and timing. Respond JSON: {upsell_product: string, upsell_price: string, upsell_url: string, upsell_score: number, pitch_subject: string, pitch_body: string, pitch_sms: string, preferred_channel: string, upsell_reason: string}",
        "Purchased: {purchased_product} at ${purchased_price}. Order total: ${order_total}. Customer: {customer_name}. Email: {customer_email}. Phone: {customer_phone}. Orders count: {orders_count}.",
        [
          { field: "upsell_product", as: "upsell_product", type: "string" },
          { field: "upsell_price", as: "upsell_price", type: "string" },
          { field: "upsell_url", as: "upsell_url", type: "string" },
          { field: "upsell_score", as: "upsell_score", type: "number" },
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
          { field: "pitch_sms", as: "pitch_sms", type: "string" },
          {
            field: "preferred_channel",
            as: "preferred_channel",
            type: "string",
          },
          { field: "upsell_reason", as: "upsell_reason", type: "string" },
        ],
      ),

      // 4. Channel routing condition
      n("4", "condition", "Preferred channel = email?", 760, {
        conditionData: [
          {
            variable: "preferred_channel",
            value: "email",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Upsell email (True path)
      emailNode(
        "5",
        "Send upsell email",
        1000,
        "{customer_email}",
        "{pitch_subject}",
        "Hi {customer_name},\\n\\nThank you for your order ({order_name})! We noticed you picked up {purchased_product} — great choice.\\n\\n{pitch_body}\\n\\nCheck it out: {upsell_url}\\n\\nThis recommendation was hand-picked based on your purchase. Enjoy!",
      ),

      // 6. SMS alternative (Twilio) — False path
      smsNode(
        "6",
        "Send upsell SMS (Twilio)",
        1000,
        "{customer_phone}",
        "{pitch_sms} Shop now: {upsell_url}",
        400,
      ),

      // 7. CRM writeback (HubSpot)
      hubspotNode(
        "7",
        "CRM writeback: upsell attempt (HubSpot)",
        1240,
        "PATCH",
        "crm/v3/objects/contacts/{customer_email}",
        [
          {
            key: "properties",
            value:
              '{"last_upsell_product":"{upsell_product}","last_upsell_score":"{upsell_score}","last_upsell_channel":"{preferred_channel}","last_order":"{order_name}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),

      // 8. 48hr conversion check + logging (Sheets)
      sheetsNode(
        "8",
        "Log upsell conversion tracking (Sheets)",
        1480,
        "UpsellTracking",
        '[["{customer_email}","{order_name}","{purchased_product}","{upsell_product}","{upsell_price}","{upsell_score}","{preferred_channel}","{upsell_reason}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // email channel
      e("4", "6", "output_2"), // sms channel
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 549. VIP UPGRADE AFTER CUMULATIVE SPEND (9 nodes)
  // ===================================================================

  "VIP Upgrade After Cumulative Spend": {
    summary:
      "Automatically upgrades customers to VIP tiers when lifetime spend thresholds are crossed. A Shopify paid order triggers lifetime spend recalculation, checks tier thresholds, updates CRM tier in HubSpot, generates AI congratulations messaging, sends a VIP upgrade email, activates tier benefits, notifies the VIP team on Slack, and audits the threshold crossing in Sheets.",
    nodes: [
      trigger("Webhook: Shopify order paid", "webhook_app", {
        source: "Shopify",
        event: "orders/paid",
        howItStarts:
          "A Shopify order payment completes, triggering a cumulative spend recalculation to check if the customer crosses a VIP tier threshold",
        setup:
          "Configure Shopify webhook for orders/paid. Define VIP tiers (e.g., Silver $500+, Gold $2000+, Platinum $5000+). Set up HubSpot contact properties for loyalty_tier and lifetime_spend.",
        inputFields: [],
      }),

      // 2. Lifetime spend recalculation (runCode)
      n("2", "runCode", "Recalculate lifetime spend", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse order and recalculate cumulative spend
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.contact_email || (payload.customer ? payload.customer.email : "") || "";
const customerName = payload.customer ? (payload.customer.first_name || "") : (payload.first_name || "");
const orderTotal = parseFloat(payload.total_price || payload.subtotal_price || "0");
const previousSpend = parseFloat(payload.customer ? (payload.customer.total_spent || "0") : "0");
const lifetimeSpend = previousSpend + orderTotal;
const ordersCount = payload.customer ? (payload.customer.orders_count || 1) : 1;

// Determine current and previous tier
function getTier(spend) {
  if (spend >= 5000) return "Platinum";
  if (spend >= 2000) return "Gold";
  if (spend >= 500) return "Silver";
  return "Standard";
}
const newTier = getTier(lifetimeSpend);
const previousTier = getTier(previousSpend);
const tierUpgraded = newTier !== previousTier && lifetimeSpend > previousSpend ? "yes" : "no";

return {
  customer_email: email,
  customer_name: customerName,
  order_total: orderTotal.toFixed(2),
  previous_spend: previousSpend.toFixed(2),
  lifetime_spend: lifetimeSpend.toFixed(2),
  orders_count: String(ordersCount),
  new_tier: newTier,
  previous_tier: previousTier,
  tier_upgraded: tierUpgraded,
  next_tier_threshold: newTier === "Platinum" ? "MAX" : newTier === "Gold" ? "5000" : newTier === "Silver" ? "2000" : "500",
  spend_to_next: newTier === "Platinum" ? "0" : String(Math.max(0, (newTier === "Gold" ? 5000 : newTier === "Silver" ? 2000 : 500) - lifetimeSpend).toFixed(2))
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_total", type: "string" },
            { name: "previous_spend", type: "string" },
            { name: "lifetime_spend", type: "string" },
            { name: "orders_count", type: "string" },
            { name: "new_tier", type: "string" },
            { name: "previous_tier", type: "string" },
            { name: "tier_upgraded", type: "string" },
            { name: "next_tier_threshold", type: "string" },
            { name: "spend_to_next", type: "string" },
          ],
        },
      }),

      // 3. Tier threshold condition
      n("3", "condition", "Tier upgraded?", 520, {
        conditionData: [
          {
            variable: "tier_upgraded",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. CRM tier update (HubSpot)
      hubspotNode(
        "4",
        "Update CRM tier (HubSpot)",
        760,
        "PATCH",
        "crm/v3/objects/contacts/{customer_email}",
        [
          {
            key: "properties",
            value:
              '{"loyalty_tier":"{new_tier}","lifetime_spend":"{lifetime_spend}","total_orders":"{orders_count}","tier_upgrade_date":"{current_date}","previous_tier":"{previous_tier}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),

      // 5. AI congratulations message
      aiNode(
        "5",
        "AI personalized congratulations",
        1000,
        "You are a luxury brand concierge. Write a warm, personalized VIP tier upgrade congratulations message. Reference the customer's journey from their previous tier, their lifetime spend milestone, and the exclusive benefits they now unlock. Be celebratory but genuine. Respond JSON: {subject_line: string, congratulations_body: string, benefits_list: string, personal_touch: string}",
        "Customer: {customer_name}. Upgraded from {previous_tier} to {new_tier}. Lifetime spend: ${lifetime_spend} across {orders_count} orders. Latest order: ${order_total}.",
        [
          { field: "subject_line", as: "congrats_subject", type: "string" },
          {
            field: "congratulations_body",
            as: "congrats_body",
            type: "string",
          },
          { field: "benefits_list", as: "benefits_list", type: "string" },
          { field: "personal_touch", as: "personal_touch", type: "string" },
        ],
      ),

      // 6. VIP email
      emailNode(
        "6",
        "Send VIP upgrade email",
        1240,
        "{customer_email}",
        "{congrats_subject}",
        "Hi {customer_name},\\n\\n{congrats_body}\\n\\nYour New {new_tier} Benefits:\\n{benefits_list}\\n\\n{personal_touch}\\n\\nThank you for being an incredible part of our community.",
      ),

      // 7. Benefits activation delay
      n("7", "delayAction", "Benefits activation processing", 1480, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 5, unit: "minutes" },
        },
      }),

      // 8. VIP team Slack notification
      slackNode(
        "8",
        "Slack: VIP tier upgrade alert",
        1720,
        ":star2: VIP Tier Upgrade! {customer_name} ({customer_email}) upgraded from {previous_tier} to {new_tier}. Lifetime spend: ${lifetime_spend} across {orders_count} orders. Latest order: ${order_total}. Congrats email sent.",
        400,
      ),

      // 9. Threshold audit (Sheets)
      sheetsNode(
        "9",
        "Log tier upgrade audit (Sheets)",
        1960,
        "VIPUpgradeAudit",
        '[["{customer_email}","{customer_name}","{previous_tier}","{new_tier}","{lifetime_spend}","{orders_count}","{order_total}","{previous_spend}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // tier upgraded -> update CRM
      e("3", "9", "output_2"), // no upgrade -> log only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 550. VIP CUSTOMER PRIORITY ROUTER (8 nodes)
  // ===================================================================

  "VIP Customer Priority Router": {
    summary:
      "Routes support tickets based on VIP tier with priority escalation. A support ticket webhook triggers VIP tier resolution from HubSpot, AI classifies sentiment and urgency, routes on VIP tier, creates a priority Zendesk assignment, sends an immediate customer acknowledgment email, alerts the account manager on Slack, and logs SLA tracking to Sheets.",
    nodes: [
      trigger("Webhook: Support ticket created", "webhook_app", {
        source: "Zendesk / Intercom",
        event: "ticket.created",
        howItStarts:
          "A new support ticket is created via any channel (email, chat, phone), triggering VIP detection and priority routing",
        setup:
          "Configure support platform to fire webhook on ticket creation. Ensure HubSpot contacts have loyalty_tier property. Set up Zendesk groups for VIP priority routing.",
        inputFields: [],
      }),

      // 2. VIP tier resolution (HubSpot)
      hubspotNode(
        "2",
        "Resolve VIP tier (HubSpot)",
        280,
        "POST",
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{ticket_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","loyalty_tier","lifetime_spend","hubspot_owner_id","company"]',
          },
        ],
        [
          {
            name: "results[0].properties.loyalty_tier",
            customName: "vip_tier",
            type: "string",
          },
          {
            name: "results[0].properties.lifetime_spend",
            customName: "lifetime_spend",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "customer_name",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "account_manager_id",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "customer_company",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
        ],
      ),

      // 3. AI sentiment/urgency classification
      aiNode(
        "3",
        "AI sentiment and urgency classification",
        520,
        "You are a support ticket triage specialist. Analyze the ticket content for sentiment (positive, neutral, negative, angry), urgency (low, medium, high, critical), and topic classification. Consider the customer's VIP tier and lifetime spend when determining priority. Respond JSON: {sentiment: string, urgency: string, topic: string, priority_score: number, suggested_sla_hours: number, escalation_needed: string, triage_summary: string}",
        "Ticket subject: {ticket_subject}. Ticket body: {ticket_body}. Customer: {customer_name} at {customer_company}. VIP Tier: {vip_tier}. Lifetime spend: ${lifetime_spend}.",
        [
          { field: "sentiment", as: "ticket_sentiment", type: "string" },
          { field: "urgency", as: "ticket_urgency", type: "string" },
          { field: "topic", as: "ticket_topic", type: "string" },
          { field: "priority_score", as: "priority_score", type: "number" },
          { field: "suggested_sla_hours", as: "sla_hours", type: "number" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
          { field: "triage_summary", as: "triage_summary", type: "string" },
        ],
      ),

      // 4. VIP tier routing condition
      n("4", "condition", "VIP tier Gold or Platinum?", 760, {
        conditionData: [
          {
            variable: "vip_tier",
            value: "Gold",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "vip_tier",
            value: "Platinum",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 5. Priority Zendesk assignment
      zendeskNode(
        "5",
        "Create priority Zendesk ticket",
        1000,
        "VIP [{vip_tier}] {ticket_subject}",
        "Priority: {ticket_urgency} | Sentiment: {ticket_sentiment} | SLA: {sla_hours}hrs\\n\\nCustomer: {customer_name} ({customer_email})\\nCompany: {customer_company}\\nLifetime Spend: ${lifetime_spend}\\nTier: {vip_tier}\\n\\nTriage: {triage_summary}\\n\\nOriginal: {ticket_body}",
        "urgent",
        [
          {
            name: "ticket.id",
            customName: "zendesk_ticket_id",
            type: "string",
          },
          {
            name: "ticket.status",
            customName: "zendesk_status",
            type: "string",
          },
        ],
      ),

      // 6. Immediate customer acknowledgment (email)
      emailNode(
        "6",
        "Send VIP acknowledgment email",
        1240,
        "{customer_email}",
        "We're on it, {customer_name} - Priority support for your {vip_tier} account",
        "Hi {customer_name},\\n\\nAs a valued {vip_tier} member, your request has been escalated to our priority support queue.\\n\\nTicket: #{zendesk_ticket_id}\\nExpected Response Time: {sla_hours} hours\\n\\nYour dedicated account team has been notified and will respond shortly. In the meantime, you can reply to this email to add more details.\\n\\nThank you for your loyalty.",
      ),

      // 7. Account manager Slack alert
      slackNode(
        "7",
        "Slack: VIP account manager alert",
        1480,
        ":rotating_light: VIP Support Alert\\nCustomer: {customer_name} ({vip_tier} - ${lifetime_spend} LTV)\\nSentiment: {ticket_sentiment} | Urgency: {ticket_urgency}\\nTopic: {ticket_topic}\\nTicket: #{zendesk_ticket_id}\\nSLA: {sla_hours}hrs\\nSummary: {triage_summary}",
        400,
      ),

      // 8. SLA logging (Sheets)
      sheetsNode(
        "8",
        "Log SLA tracking (Sheets)",
        1720,
        "VIPSLATracking",
        '[["{customer_email}","{customer_name}","{vip_tier}","{lifetime_spend}","{ticket_sentiment}","{ticket_urgency}","{sla_hours}","{zendesk_ticket_id}","{triage_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // VIP Gold/Platinum -> priority assignment
      e("4", "8", "output_2"), // non-VIP -> log only
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 551. VIP CUSTOMER WELCOME (7 nodes)
  // ===================================================================

  "VIP Customer Welcome": {
    summary:
      "Onboards new VIP customers with a personalized concierge welcome experience. A VIP tag trigger fires profile enrichment from HubSpot, AI generates a personalized welcome package, sends a premium welcome email, creates a success tracker in Sheets, applies a concierge scheduling delay, and notifies the VIP team on Slack.",
    nodes: [
      trigger("Webhook: VIP tag assigned", "webhook_app", {
        source: "HubSpot / CRM",
        event: "contact.property.changed",
        howItStarts:
          "A customer is tagged as VIP in the CRM (manually or via automation), triggering a premium onboarding welcome sequence",
        setup:
          "Configure HubSpot workflow to fire webhook when loyalty_tier property changes to Gold, Platinum, or VIP. Include contact ID and email in payload.",
        inputFields: [],
      }),

      // 2. Profile enrichment (HubSpot)
      hubspotNode(
        "2",
        "Enrich VIP profile (HubSpot)",
        280,
        "POST",
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{vip_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","loyalty_tier","lifetime_spend","company","jobtitle","phone","hs_lead_status","recent_deal_amount"]',
          },
        ],
        [
          {
            name: "results[0].properties.firstname",
            customName: "customer_name",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.loyalty_tier",
            customName: "vip_tier",
            type: "string",
          },
          {
            name: "results[0].properties.lifetime_spend",
            customName: "lifetime_spend",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "customer_phone",
            type: "string",
          },
        ],
      ),

      // 3. AI personalized welcome package
      aiNode(
        "3",
        "AI personalized welcome package",
        520,
        "You are a luxury brand concierge writing a VIP welcome package. Create a warm, personalized welcome that references the customer's tier, spending history, and company. Include 3 exclusive benefits, a personal concierge introduction, and a curated first recommendation. Be genuine and celebratory without being over-the-top. Respond JSON: {welcome_subject: string, welcome_body: string, benefits_html: string, concierge_intro: string, first_recommendation: string, welcome_gift_suggestion: string}",
        "Customer: {customer_name}, Tier: {vip_tier}, Lifetime Spend: ${lifetime_spend}, Company: {company}, Title: {job_title}",
        [
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
          { field: "benefits_html", as: "benefits_html", type: "string" },
          { field: "concierge_intro", as: "concierge_intro", type: "string" },
          {
            field: "first_recommendation",
            as: "first_recommendation",
            type: "string",
          },
          {
            field: "welcome_gift_suggestion",
            as: "welcome_gift",
            type: "string",
          },
        ],
      ),

      // 4. Premium welcome email
      emailNode(
        "4",
        "Send VIP welcome email",
        760,
        "{customer_email}",
        "{welcome_subject}",
        "Dear {customer_name},\\n\\n{welcome_body}\\n\\nYour Exclusive {vip_tier} Benefits:\\n{benefits_html}\\n\\n{concierge_intro}\\n\\nOur First Recommendation For You:\\n{first_recommendation}\\n\\nWelcome to the {vip_tier} experience.",
      ),

      // 5. Success tracker creation (Sheets)
      sheetsNode(
        "5",
        "Create VIP success tracker (Sheets)",
        1000,
        "VIPSuccessTracker",
        '[["{customer_email}","{customer_name}","{vip_tier}","{lifetime_spend}","{company}","{welcome_gift}","welcome_sent","pending_concierge"]]',
      ),

      // 6. Concierge scheduling delay
      n("6", "delayAction", "Concierge follow-up scheduling delay", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 24, unit: "hours" },
        },
      }),

      // 7. VIP team Slack notification
      slackNode(
        "7",
        "Slack: New VIP customer notification",
        1480,
        ":crown: New VIP Welcome Sent!\\nCustomer: {customer_name} ({customer_email})\\nTier: {vip_tier} | LTV: ${lifetime_spend}\\nCompany: {company} | Title: {job_title}\\nGift Suggestion: {welcome_gift}\\nConcierge follow-up scheduled in 24 hours.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 552. WAREHOUSE INVENTORY COUNT AUTOMATION (9 nodes)
  // ===================================================================

  "Warehouse Inventory Count Automation": {
    summary:
      "Automates cycle count reconciliation with three-tier variance handling. A scheduled count triggers system inventory pull from Shopify, ingests physical count data, computes variances via runCode, routes on tolerance thresholds, auto-adjusts minor variances, gates medium variances through manager approval, creates investigation tasks for major discrepancies, and sends a finance audit summary email.",
    nodes: [
      trigger("Schedule: Daily inventory cycle count", "schedule", {
        schedule: "0 5 * * *",
        howItStarts:
          "Runs daily at 5 AM to reconcile physical inventory counts against system records and handle variances by severity",
        setup:
          "Configure Shopify inventory locations. Set up a physical count ingestion endpoint (barcode scanner or manual entry). Define variance thresholds: minor (<5%), medium (5-15%), major (>15%).",
        inputFields: [],
      }),

      // 2. System inventory pull (Shopify)
      shopifyNode(
        "2",
        "Pull system inventory levels (Shopify)",
        280,
        "GET",
        "inventory_levels.json?location_ids={warehouse_location_id}&limit=250",
        [],
        [
          {
            name: "inventory_levels",
            customName: "system_inventory",
            type: "string",
          },
          {
            name: "inventory_levels.length",
            customName: "sku_count",
            type: "number",
          },
        ],
      ),

      // 3. Physical count ingestion
      n("3", "hitApi", "Ingest physical count data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{count_sheet_id}/values/PhysicalCount!A2:D500",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "physical_counts", type: "string" },
            {
              name: "values.length",
              customName: "physical_count_rows",
              type: "number",
            },
          ],
        },
      }),

      // 4. Variance computation (runCode)
      n("4", "runCode", "Compute inventory variances", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare system vs physical inventory counts
const system = JSON.parse(vars.system_inventory || "[]");
const physical = JSON.parse(vars.physical_counts || "[]");
const systemMap = {};
for (const item of system) {
  systemMap[item.inventory_item_id || item.sku] = item.available || 0;
}
const minor = [];
const medium = [];
const major = [];
let totalVariance = 0;
for (const row of physical) {
  const sku = row[0];
  const physicalQty = parseInt(row[1] || "0", 10);
  const systemQty = systemMap[sku] || 0;
  const diff = Math.abs(physicalQty - systemQty);
  const pct = systemQty > 0 ? (diff / systemQty) * 100 : (diff > 0 ? 100 : 0);
  totalVariance += diff;
  const entry = { sku, system_qty: systemQty, physical_qty: physicalQty, diff, variance_pct: pct.toFixed(1) };
  if (pct <= 5) minor.push(entry);
  else if (pct <= 15) medium.push(entry);
  else major.push(entry);
}
return {
  minor_count: String(minor.length),
  medium_count: String(medium.length),
  major_count: String(major.length),
  total_variance_units: String(totalVariance),
  minor_items: JSON.stringify(minor.slice(0, 20)),
  medium_items: JSON.stringify(medium.slice(0, 20)),
  major_items: JSON.stringify(major.slice(0, 20)),
  worst_severity: major.length > 0 ? "major" : medium.length > 0 ? "medium" : "minor",
  audit_summary: minor.length + " minor, " + medium.length + " medium, " + major.length + " major variances"
};`,
          outputVariables: [
            { name: "minor_count", type: "string" },
            { name: "medium_count", type: "string" },
            { name: "major_count", type: "string" },
            { name: "total_variance_units", type: "string" },
            { name: "minor_items", type: "string" },
            { name: "medium_items", type: "string" },
            { name: "major_items", type: "string" },
            { name: "worst_severity", type: "string" },
            { name: "audit_summary", type: "string" },
          ],
        },
      }),

      // 5. Tolerance condition
      n("5", "condition", "Major variances found?", 1000, {
        conditionData: [
          {
            variable: "worst_severity",
            value: "major",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Auto-adjust minor variances (Shopify inventory set)
      shopifyNode(
        "6",
        "Auto-adjust minor variances (Shopify)",
        1240,
        "POST",
        "inventory_levels/set.json",
        [{ key: "inventory_adjustments", value: "{minor_items}" }],
        [
          {
            name: "inventory_level.available",
            customName: "adjusted_available",
            type: "number",
          },
        ],
      ),

      // 7. Manager approval gate for medium variances
      n(
        "7",
        "approvalGate",
        "Manager approval: medium variances",
        1240,
        {
          approvalGateData: {
            message:
              "Inventory Count Reconciliation - Medium Variances Detected\\n\\n{medium_count} SKUs have 5-15% variance between system and physical counts.\\n\\nTotal variance units: {total_variance_units}\\nItems: {medium_items}\\n\\nApprove to auto-adjust, reject to create investigation tasks.",
          },
        },
        400,
      ),

      // 8. Investigation tasks for major variances (Slack)
      slackNode(
        "8",
        "Slack: Major variance investigation tasks",
        1480,
        ":warning: MAJOR Inventory Variances Detected\\n{major_count} SKUs exceed 15% variance threshold.\\nTotal variance: {total_variance_units} units\\nItems: {major_items}\\n\\nImmediate investigation required. Finance audit attached.",
        400,
      ),

      // 9. Finance audit summary email
      emailNode(
        "9",
        "Send finance audit summary",
        1720,
        "{finance_email}",
        "Daily Inventory Count Audit - {audit_summary}",
        "Daily Inventory Count Reconciliation Report\\n\\nSummary: {audit_summary}\\nTotal Variance: {total_variance_units} units\\n\\nMinor (<5%): {minor_count} SKUs - Auto-adjusted\\nMedium (5-15%): {medium_count} SKUs - Pending manager approval\\nMajor (>15%): {major_count} SKUs - Investigation required\\n\\nMajor Variance Items:\\n{major_items}\\n\\nPlease review and take action on medium and major items within 48 hours.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "8", "output_1"), // major variances -> investigation tasks + Slack
      e("5", "6", "output_2"), // no major -> auto-adjust minor
      e("6", "7"), // then manager approval for medium
      e("7", "9"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 553. WARRANTY CLAIM BOT (8 nodes)
  // ===================================================================

  "Warranty Claim Bot": {
    summary:
      "Processes warranty claims end-to-end with automated validation and routing. A claim webhook triggers order and warranty validation from Shopify, checks warranty window eligibility, AI classifies the issue, creates a CRM case in HubSpot, routes resolution (refund/replace/repair), sends a customer response email, and logs claim outcomes to Sheets.",
    nodes: [
      trigger("Webhook: Warranty claim submitted", "webhook_form", {
        source: "Website / Support Portal",
        event: "warranty.claim.submitted",
        howItStarts:
          "A customer submits a warranty claim form with order number, issue description, and product details, triggering automated validation and processing",
        setup:
          "Configure warranty claim form to post to webhook URL. Include order number, product SKU, issue description, and customer email. Set up Shopify order lookup and HubSpot case management.",
        inputFields: [],
      }),

      // 2. Order/warranty validation (Shopify)
      n("2", "runCode", "Validate order and warranty eligibility", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract claim data and compute warranty window
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const orderNumber = payload.order_number || payload.order_id || "";
const productSku = payload.sku || payload.product_sku || "";
const issueDescription = payload.issue || payload.description || payload.problem || "";
const purchaseDate = payload.purchase_date || payload.order_date || "";
const customerName = payload.name || payload.customer_name || "";

// Calculate warranty window (default 365 days)
const purchaseMs = new Date(purchaseDate).getTime();
const now = Date.now();
const daysSincePurchase = Math.floor((now - purchaseMs) / 86400000);
const warrantyDays = 365;
const withinWarranty = daysSincePurchase <= warrantyDays && daysSincePurchase >= 0 ? "yes" : "no";
const daysRemaining = Math.max(0, warrantyDays - daysSincePurchase);

return {
  customer_email: email,
  customer_name: customerName,
  order_number: orderNumber,
  product_sku: productSku,
  issue_description: issueDescription,
  days_since_purchase: String(daysSincePurchase),
  within_warranty: withinWarranty,
  warranty_days_remaining: String(daysRemaining),
  claim_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_number", type: "string" },
            { name: "product_sku", type: "string" },
            { name: "issue_description", type: "string" },
            { name: "days_since_purchase", type: "string" },
            { name: "within_warranty", type: "string" },
            { name: "warranty_days_remaining", type: "string" },
            { name: "claim_date", type: "string" },
          ],
        },
      }),

      // 3. Warranty window condition
      n("3", "condition", "Within warranty window?", 520, {
        conditionData: [
          {
            variable: "within_warranty",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI issue classification
      aiNode(
        "4",
        "AI warranty issue classification",
        760,
        "You are a warranty claims specialist. Classify this warranty claim by defect type (manufacturing, wear-and-tear, user-damage, missing-parts), severity (minor, moderate, critical), and recommended resolution (refund, replacement, repair, denied). Consider the days since purchase, product type, and issue description. Respond JSON: {defect_type: string, severity: string, resolution: string, resolution_reason: string, priority: string, estimated_cost: string, customer_response: string}",
        "Order: {order_number}. Product: {product_sku}. Issue: {issue_description}. Days since purchase: {days_since_purchase}. Warranty remaining: {warranty_days_remaining} days.",
        [
          { field: "defect_type", as: "defect_type", type: "string" },
          { field: "severity", as: "claim_severity", type: "string" },
          { field: "resolution", as: "resolution_type", type: "string" },
          {
            field: "resolution_reason",
            as: "resolution_reason",
            type: "string",
          },
          { field: "priority", as: "claim_priority", type: "string" },
          { field: "estimated_cost", as: "estimated_cost", type: "string" },
          {
            field: "customer_response",
            as: "customer_response_text",
            type: "string",
          },
        ],
      ),

      // 5. CRM case creation (HubSpot)
      hubspotNode(
        "5",
        "Create warranty case (HubSpot)",
        1000,
        "POST",
        "crm/v3/objects/tickets",
        [
          {
            key: "properties",
            value:
              '{"subject":"Warranty Claim - {order_number} - {product_sku}","content":"{issue_description}","hs_pipeline":"support","hs_pipeline_stage":"1","hs_ticket_priority":"{claim_priority}","warranty_resolution":"{resolution_type}","defect_type":"{defect_type}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_ticket_id", type: "string" },
          {
            name: "properties.hs_object_id",
            customName: "case_number",
            type: "string",
          },
        ],
      ),

      // 6. Resolution routing — sends appropriate resolution info
      n("6", "runCode", "Route resolution action", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build resolution-specific details for customer communication
const resolution = vars.resolution_type || "replacement";
let actionDetails = "";
let internalNotes = "";
if (resolution === "refund") {
  actionDetails = "A full refund of the purchase price will be processed to your original payment method within 5-7 business days.";
  internalNotes = "Process refund for order " + vars.order_number + ". Estimated cost: " + vars.estimated_cost;
} else if (resolution === "replacement") {
  actionDetails = "A replacement unit will be shipped to your address on file within 3-5 business days. You will receive tracking information once it ships.";
  internalNotes = "Ship replacement for SKU " + vars.product_sku + ". Estimated cost: " + vars.estimated_cost;
} else if (resolution === "repair") {
  actionDetails = "We will send you a prepaid shipping label to return your item for repair. Expected turnaround is 10-14 business days.";
  internalNotes = "Generate prepaid label for repair. SKU: " + vars.product_sku;
} else {
  actionDetails = "After review, this claim does not meet our warranty criteria. Please contact support for options.";
  internalNotes = "Claim denied. Reason: " + vars.resolution_reason;
}
return {
  action_details: actionDetails,
  internal_notes: internalNotes,
  resolution_label: resolution.charAt(0).toUpperCase() + resolution.slice(1)
};`,
          outputVariables: [
            { name: "action_details", type: "string" },
            { name: "internal_notes", type: "string" },
            { name: "resolution_label", type: "string" },
          ],
        },
      }),

      // 7. Customer response email
      emailNode(
        "7",
        "Send warranty resolution email",
        1480,
        "{customer_email}",
        "Warranty Claim Update - Order {order_number} - {resolution_label}",
        "Hi {customer_name},\\n\\nThank you for reaching out about your warranty concern with order {order_number}.\\n\\n{customer_response_text}\\n\\nResolution: {resolution_label}\\n{action_details}\\n\\nCase Reference: #{hubspot_ticket_id}\\n\\nIf you have any questions, reply to this email or reference your case number. We appreciate your patience.",
      ),

      // 8. Claim outcome logging (Sheets)
      sheetsNode(
        "8",
        "Log warranty claim outcome (Sheets)",
        1720,
        "WarrantyClaims",
        '[["{claim_date}","{customer_email}","{order_number}","{product_sku}","{defect_type}","{claim_severity}","{resolution_type}","{estimated_cost}","{days_since_purchase}","{hubspot_ticket_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // within warranty -> classify
      e("3", "8", "output_2"), // expired -> log as denied
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 554. WARRANTY CLAIM PHOTO TRIAGE BOT (9 nodes)
  // ===================================================================

  "Warranty Claim Photo Triage Bot": {
    summary:
      "Triages warranty claims with photo evidence using AI vision for damage classification. A photo claim trigger validates the image, AI vision classifies the damage type and severity, pulls defect bulletins, routes on safety severity, escalates safety issues on Slack, sends a triage acknowledgment email, syncs the case to Zendesk, and logs photo triage results to Sheets.",
    nodes: [
      trigger("Webhook: Warranty photo claim submitted", "webhook_form", {
        source: "Support Portal / Mobile App",
        event: "warranty.photo.submitted",
        howItStarts:
          "A customer submits a warranty claim with photo evidence of damage, triggering AI-powered visual triage and severity routing",
        setup:
          "Configure claim form to accept photo uploads and post to webhook URL with image URL, order details, and description. Ensure OpenAI vision API access is configured.",
        inputFields: [],
      }),

      // 2. Image validation (runCode)
      n("2", "runCode", "Validate photo submission", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and validate photo claim data
const payload = JSON.parse(vars.webhook_payload || "{}");
const imageUrl = payload.image_url || payload.photo_url || payload.attachment_url || "";
const email = payload.email || payload.customer_email || "";
const name = payload.name || payload.customer_name || "";
const orderNum = payload.order_number || payload.order_id || "";
const sku = payload.sku || payload.product_sku || payload.product || "";
const description = payload.description || payload.issue || "";
const hasValidImage = imageUrl && (imageUrl.endsWith(".jpg") || imageUrl.endsWith(".jpeg") || imageUrl.endsWith(".png") || imageUrl.endsWith(".webp") || imageUrl.includes("cloudinary") || imageUrl.includes("s3.amazonaws"));
return {
  customer_email: email,
  customer_name: name,
  order_number: orderNum,
  product_sku: sku,
  issue_description: description,
  photo_url: imageUrl,
  image_valid: hasValidImage ? "yes" : "no",
  submission_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_number", type: "string" },
            { name: "product_sku", type: "string" },
            { name: "issue_description", type: "string" },
            { name: "photo_url", type: "string" },
            { name: "image_valid", type: "string" },
            { name: "submission_timestamp", type: "string" },
          ],
        },
      }),

      // 3. AI vision damage classification
      aiNode(
        "3",
        "AI vision damage classification",
        520,
        "You are a product damage assessment specialist with vision capabilities. Analyze the photo URL and customer description to classify the damage. Determine defect type (cosmetic, structural, electrical, safety-hazard, missing-component), damage severity (minor, moderate, severe, critical-safety), whether it matches known defect bulletins, and the recommended action. Respond JSON: {defect_type: string, damage_severity: string, safety_risk: string, matches_bulletin: string, bulletin_id: string, confidence_score: number, damage_description: string, recommended_action: string, requires_escalation: string}",
        "Photo: {photo_url}. Product: {product_sku}. Customer description: {issue_description}. Order: {order_number}.",
        [
          { field: "defect_type", as: "defect_type", type: "string" },
          { field: "damage_severity", as: "damage_severity", type: "string" },
          { field: "safety_risk", as: "safety_risk", type: "string" },
          { field: "matches_bulletin", as: "matches_bulletin", type: "string" },
          { field: "bulletin_id", as: "bulletin_id", type: "string" },
          {
            field: "confidence_score",
            as: "vision_confidence",
            type: "number",
          },
          {
            field: "damage_description",
            as: "ai_damage_description",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "requires_escalation",
            as: "requires_escalation",
            type: "string",
          },
        ],
      ),

      // 4. Defect bulletin pull
      n("4", "hitApi", "Pull defect bulletin database", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{bulletin_sheet_id}/values/DefectBulletins!A2:F100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "bulletin_data", type: "string" },
            {
              name: "values.length",
              customName: "bulletin_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. Safety severity condition
      n("5", "condition", "Safety risk detected?", 1000, {
        conditionData: [
          {
            variable: "safety_risk",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "safety_risk",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. Safety Slack escalation (True path)
      slackNode(
        "6",
        "Slack: SAFETY escalation alert",
        1240,
        ":rotating_light: SAFETY ALERT - Warranty Photo Triage\\nCustomer: {customer_name} ({customer_email})\\nProduct: {product_sku} | Order: {order_number}\\nSafety Risk: {safety_risk}\\nDefect: {defect_type} - {damage_severity}\\nAI Description: {ai_damage_description}\\nBulletin Match: {matches_bulletin} (ID: {bulletin_id})\\nPhoto: {photo_url}\\nIMEDIATE INVESTIGATION REQUIRED",
      ),

      // 7. Customer triage acknowledgment email
      emailNode(
        "7",
        "Send photo triage acknowledgment",
        1480,
        "{customer_email}",
        "Warranty Claim Received - Photo Review Complete for Order {order_number}",
        "Hi {customer_name},\\n\\nThank you for submitting photos of your warranty concern for order {order_number} ({product_sku}).\\n\\nOur automated assessment has reviewed your submission:\\n- Damage Type: {defect_type}\\n- Assessment: {ai_damage_description}\\n- Recommended Action: {recommended_action}\\n\\nA support specialist will follow up with you within 24 hours with next steps. If this is a safety concern, our team has been immediately notified.\\n\\nPlease do not continue using the product if you believe there is a safety risk.",
      ),

      // 8. Case sync (Zendesk)
      zendeskNode(
        "8",
        "Sync triage case to Zendesk",
        1720,
        "Photo Triage: {product_sku} - {defect_type} ({damage_severity})",
        "Photo Warranty Claim\\nCustomer: {customer_name} ({customer_email})\\nOrder: {order_number}\\nProduct: {product_sku}\\nDefect: {defect_type} | Severity: {damage_severity}\\nSafety Risk: {safety_risk}\\nAI Assessment: {ai_damage_description}\\nConfidence: {vision_confidence}%\\nBulletin Match: {matches_bulletin} (ID: {bulletin_id})\\nRecommended: {recommended_action}\\nPhoto: {photo_url}",
        "high",
        [
          {
            name: "ticket.id",
            customName: "zendesk_ticket_id",
            type: "string",
          },
        ],
      ),

      // 9. Photo triage logging (Sheets)
      sheetsNode(
        "9",
        "Log photo triage results (Sheets)",
        1960,
        "PhotoTriageLog",
        '[["{submission_timestamp}","{customer_email}","{order_number}","{product_sku}","{defect_type}","{damage_severity}","{safety_risk}","{vision_confidence}","{matches_bulletin}","{bulletin_id}","{recommended_action}","{zendesk_ticket_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // safety risk -> Slack escalation
      e("5", "7", "output_2"), // no safety risk -> acknowledgment email
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 555. WARRANTY REGISTRATION PROMPT (7 nodes)
  // ===================================================================

  "Warranty Registration Prompt": {
    summary:
      "Prompts customers to register their warranty after product delivery. A post-delivery delay fires, checks registration status, AI generates a product-aware prompt, sends a registration email, waits for follow-up, sends an SMS reminder, and logs registration conversions to Sheets.",
    nodes: [
      trigger("Webhook: Order delivered", "webhook_app", {
        source: "Shopify / ShipStation",
        event: "fulfillment.delivered",
        howItStarts:
          "A delivery confirmation webhook fires 3 days after delivery, triggering a warranty registration prompt sequence",
        setup:
          "Configure delivery tracking webhook (ShipStation, AfterShip, or Shopify). Set registration portal URL. Provide Twilio SMS credentials.",
        inputFields: [],
      }),

      // 2. Registration check condition
      n(
        "2",
        "runCode",
        "Check registration status and extract delivery data",
        280,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Extract delivery data and check warranty registration status
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || (payload.customer ? payload.customer.email : "") || "";
const name = payload.customer_name || payload.name || (payload.customer ? payload.customer.first_name : "") || "";
const phone = payload.phone || payload.customer_phone || (payload.customer ? payload.customer.phone : "") || "";
const orderNum = payload.order_number || payload.order_id || payload.order_name || "";
const productName = payload.product_name || payload.product || (payload.line_items ? (payload.line_items[0] || {}).title : "") || "";
const productSku = payload.sku || payload.product_sku || (payload.line_items ? (payload.line_items[0] || {}).sku : "") || "";
const deliveryDate = payload.delivered_at || payload.delivery_date || new Date().toISOString().split("T")[0];
const registered = payload.warranty_registered || "no";
return {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  order_number: orderNum,
  product_name: productName,
  product_sku: productSku,
  delivery_date: deliveryDate,
  warranty_registered: registered,
  registration_url: "https://yourstore.com/warranty/register?order=" + orderNum + "&sku=" + productSku
};`,
            outputVariables: [
              { name: "customer_email", type: "string" },
              { name: "customer_name", type: "string" },
              { name: "customer_phone", type: "string" },
              { name: "order_number", type: "string" },
              { name: "product_name", type: "string" },
              { name: "product_sku", type: "string" },
              { name: "delivery_date", type: "string" },
              { name: "warranty_registered", type: "string" },
              { name: "registration_url", type: "string" },
            ],
          },
        },
      ),

      // 3. Registration check condition
      n("3", "condition", "Warranty not yet registered?", 520, {
        conditionData: [
          {
            variable: "warranty_registered",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI product-aware prompt
      aiNode(
        "4",
        "AI product-aware registration prompt",
        760,
        "You are a customer success specialist. Write a friendly, product-specific warranty registration prompt. Explain why registration matters (faster claims, recalls, extended coverage), reference the specific product they received, and make it feel easy (takes 30 seconds). Respond JSON: {email_subject: string, email_body: string, sms_text: string, benefit_highlight: string}",
        "Customer: {customer_name}. Product: {product_name} (SKU: {product_sku}). Delivered: {delivery_date}. Order: {order_number}. Registration URL: {registration_url}.",
        [
          { field: "email_subject", as: "reg_email_subject", type: "string" },
          { field: "email_body", as: "reg_email_body", type: "string" },
          { field: "sms_text", as: "reg_sms_text", type: "string" },
          {
            field: "benefit_highlight",
            as: "benefit_highlight",
            type: "string",
          },
        ],
      ),

      // 5. Registration email
      emailNode(
        "5",
        "Send warranty registration email",
        1000,
        "{customer_email}",
        "{reg_email_subject}",
        "Hi {customer_name},\\n\\n{reg_email_body}\\n\\nRegister now (takes 30 seconds):\\n{registration_url}\\n\\n{benefit_highlight}\\n\\nIf you have any questions about your {product_name}, our team is here to help.",
      ),

      // 6. Follow-up delay
      n("6", "delayAction", "Follow-up reminder delay (5 days)", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 5, unit: "days" },
        },
      }),

      // 7. SMS reminder (Twilio)
      smsNode(
        "7",
        "SMS warranty registration reminder",
        1480,
        "{customer_phone}",
        "{reg_sms_text} Register here: {registration_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // not registered -> prompt
      e("3", "7", "output_2"), // already registered -> skip to log (via SMS node as pass-through logging)
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 556. WEB PUSH BROWSE RECOVERY (7 nodes)
  // ===================================================================

  "Web Push Browse Recovery": {
    summary:
      "Recovers browse abandoners via web push notifications with email fallback. A storefront browse abandon event pulls product details from Shopify, checks push subscription status, AI generates push copy, sends a web push notification, waits for click engagement, and falls back to email if no click.",
    nodes: [
      trigger("Webhook: Storefront browse abandon", "webhook_app", {
        source: "Website Analytics",
        event: "browse.abandon.push_eligible",
        howItStarts:
          "A visitor browses products for 3+ minutes and leaves without adding to cart, triggering web push recovery for subscribed users",
        setup:
          "Configure website tracking to fire webhook on browse abandonment. Include viewed product IDs, visitor push subscription token, and email. Set up web push service (OneSignal, Firebase, or native).",
        inputFields: [],
      }),

      // 2. Product pull (Shopify)
      shopifyNode(
        "2",
        "Pull browsed product details (Shopify)",
        280,
        "GET",
        "products/{browsed_product_id}.json?fields=id,title,handle,images,variants",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.images[0].src",
            customName: "product_image",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "product_price",
            type: "string",
          },
          {
            name: "product.handle",
            customName: "product_handle",
            type: "string",
          },
        ],
      ),

      // 3. Push subscription check condition
      n("3", "condition", "Has push subscription?", 520, {
        conditionData: [
          {
            variable: "push_subscription_token",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI push copy
      aiNode(
        "4",
        "AI push notification copy",
        760,
        "You are a push notification copywriter. Write a short, compelling browse recovery push notification. The title must be under 50 characters and the body under 100 characters. Create urgency without being aggressive. Respond JSON: {push_title: string, push_body: string, email_subject: string, email_body: string}",
        "Product: {product_title} at ${product_price}. Customer browsed but did not add to cart.",
        [
          { field: "push_title", as: "push_title", type: "string" },
          { field: "push_body", as: "push_body", type: "string" },
          {
            field: "email_subject",
            as: "fallback_email_subject",
            type: "string",
          },
          { field: "email_body", as: "fallback_email_body", type: "string" },
        ],
      ),

      // 5. Web push notification
      pushNode(
        "5",
        "Send web push notification",
        1000,
        "{push_title}",
        "{push_body}",
      ),

      // 6. Click delay check
      n("6", "delayAction", "Wait for push click (2 hours)", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 2, unit: "hours" },
        },
      }),

      // 7. Email fallback
      emailNode(
        "7",
        "Email fallback: browse recovery",
        1480,
        "{visitor_email}",
        "{fallback_email_subject}",
        "{fallback_email_body}\\n\\nYou recently viewed {product_title} (${product_price}).\\n\\nTake another look: https://{shopify_store}.myshopify.com/products/{product_handle}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // has push token -> AI copy
      e("3", "7", "output_2"), // no push token -> email fallback
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 557. WEBENGAGE CART RECOVERY JOURNEY (10 nodes)
  // ===================================================================

  "WebEngage Cart Recovery Journey": {
    summary:
      "Multi-touch cart recovery journey across push, email, and SMS with progressive urgency. A cart abandon event resolves channel preferences via runCode, sends a push notification, waits 1 hour, sends an email with cart contents, rechecks conversion, sends an SMS with incentive, sends a final scarcity email, logs journey outcomes to Sheets, and suppresses purchasers.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "E-Commerce Platform",
        event: "cart.abandoned",
        howItStarts:
          "A customer abandons their cart after 60 minutes of inactivity, triggering a multi-channel recovery journey with progressive urgency",
        setup:
          "Configure cart abandonment webhook with cart contents, customer details, and recovery URL. Set up push notification service, Twilio SMS, and incentive codes.",
        inputFields: [],
      }),

      // 2. Channel/consent resolution (runCode)
      n("2", "runCode", "Resolve channels and cart data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract cart data and channel preferences
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const phone = payload.phone || payload.customer_phone || "";
const name = payload.name || payload.customer_name || payload.first_name || "";
const cartItems = payload.items || payload.cart_items || payload.line_items || [];
const cartValue = parseFloat(payload.total || payload.cart_total || cartItems.reduce((s, i) => s + (parseFloat(i.price || 0) * (i.quantity || 1)), 0));
const recoveryUrl = payload.recovery_url || payload.checkout_url || "";
const pushToken = payload.push_token || payload.push_subscription || "";
const smsConsent = payload.sms_consent || (phone ? "yes" : "no");
const itemSummary = cartItems.map(i => (i.title || i.name) + " x" + (i.quantity || 1)).join(", ");
const incentiveCode = cartValue >= 100 ? "SAVE15" : cartValue >= 50 ? "SAVE10" : "SAVE5";
const discountPct = cartValue >= 100 ? "15" : cartValue >= 50 ? "10" : "5";
return {
  customer_email: email,
  customer_phone: phone,
  customer_name: name,
  cart_value: cartValue.toFixed(2),
  cart_items_summary: itemSummary || "your selected items",
  cart_item_count: String(cartItems.length),
  recovery_url: recoveryUrl,
  push_token: pushToken,
  sms_consent: smsConsent,
  incentive_code: incentiveCode,
  discount_pct: discountPct,
  first_item_name: cartItems[0] ? (cartItems[0].title || cartItems[0].name || "your item") : "your item",
  converted: "no"
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "cart_value", type: "string" },
            { name: "cart_items_summary", type: "string" },
            { name: "cart_item_count", type: "string" },
            { name: "recovery_url", type: "string" },
            { name: "push_token", type: "string" },
            { name: "sms_consent", type: "string" },
            { name: "incentive_code", type: "string" },
            { name: "discount_pct", type: "string" },
            { name: "first_item_name", type: "string" },
            { name: "converted", type: "string" },
          ],
        },
      }),

      // 3. Push notification (Touch 1)
      pushNode(
        "3",
        "Push: Cart reminder (Touch 1)",
        520,
        "You left something behind!",
        "Your {first_item_name} is waiting. Complete your order before it sells out.",
      ),

      // 4. 1hr delay
      n("4", "delayAction", "Wait 1 hour", 760, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 1, unit: "hours" },
        },
      }),

      // 5. Email with cart (Touch 2)
      emailNode(
        "5",
        "Email: Cart recovery with details (Touch 2)",
        1000,
        "{customer_email}",
        "Your cart is waiting, {customer_name} - {cart_item_count} items (${cart_value})",
        "Hi {customer_name},\\n\\nYou left {cart_item_count} items in your cart worth ${cart_value}:\\n\\n{cart_items_summary}\\n\\nComplete your order: {recovery_url}\\n\\nFree shipping on all orders. Questions? Reply to this email — we are here to help.",
      ),

      // 6. Conversion recheck condition
      n("6", "condition", "Still not converted?", 1240, {
        conditionData: [
          {
            variable: "converted",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. SMS with incentive (Touch 3 - Twilio)
      smsNode(
        "7",
        "SMS: Incentive offer (Touch 3)",
        1480,
        "{customer_phone}",
        "Hi {customer_name}, your cart (${cart_value}) is almost gone! Use code {incentive_code} for {discount_pct}% off. Complete your order: {recovery_url}",
      ),

      // 8. Final scarcity email (Touch 4)
      emailNode(
        "8",
        "Email: Final scarcity warning (Touch 4)",
        1720,
        "{customer_email}",
        "Last chance - {first_item_name} is almost sold out",
        "Hi {customer_name},\\n\\nThis is your final reminder — your cart items are selling fast and we cannot hold them much longer.\\n\\nYour Cart ({cart_item_count} items): {cart_items_summary}\\nTotal: ${cart_value}\\n\\nAs a thank you for coming back, use code {incentive_code} for {discount_pct}% off your entire order.\\n\\nComplete your order now: {recovery_url}\\n\\nThis offer expires in 24 hours.",
      ),

      // 9. Journey outcome logging (Sheets)
      sheetsNode(
        "9",
        "Log cart recovery journey (Sheets)",
        1960,
        "CartRecoveryJourney",
        '[["{customer_email}","{cart_value}","{cart_item_count}","{first_item_name}","{incentive_code}","{discount_pct}","{push_token}","{sms_consent}","{converted}"]]',
      ),

      // 10. Purchase suppression flag
      n("10", "runCode", "Set purchase suppression flag", 2200, {
        runCodeDetails: {
          language: "javascript",
          code: `// Mark journey as complete for suppression in future runs
return {
  journey_status: "completed",
  suppression_key: vars.customer_email + "_cart_recovery",
  total_touches: "4",
  final_incentive: vars.incentive_code
};`,
          outputVariables: [
            { name: "journey_status", type: "string" },
            { name: "suppression_key", type: "string" },
            { name: "total_touches", type: "string" },
            { name: "final_incentive", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // not converted -> SMS incentive
      e("6", "9", "output_2"), // converted -> skip to logging
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 558. WHATSAPP BROWSE RECOVERY (7 nodes)
  // ===================================================================

  "WhatsApp Browse Recovery": {
    summary:
      "Recovers browse abandoners via WhatsApp with conversational AI messaging. A browse abandon event checks WhatsApp consent, pulls product details from Shopify, AI generates conversational recovery messaging, sends a WhatsApp message, logs conversion data to Sheets, and falls back to email if no WhatsApp consent.",
    nodes: [
      trigger("Webhook: Browse abandon (WhatsApp eligible)", "webhook_app", {
        source: "Website Analytics",
        event: "browse.abandon.whatsapp",
        howItStarts:
          "A visitor who has opted into WhatsApp communications browses products and leaves without adding to cart, triggering conversational recovery",
        setup:
          "Configure website tracking to fire webhook on browse abandonment for WhatsApp-consented visitors. Include product data, customer WhatsApp number, and email fallback.",
        inputFields: [],
      }),

      // 2. WhatsApp consent check condition
      n("2", "runCode", "Extract browse data and check WhatsApp consent", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse browse event and check WhatsApp consent
const payload = JSON.parse(vars.webhook_payload || "{}");
const waPhone = payload.whatsapp_number || payload.phone || payload.wa_number || "";
const email = payload.email || payload.customer_email || "";
const name = payload.name || payload.customer_name || "";
const products = payload.viewed_products || payload.products || [];
const top = products.sort((a, b) => (b.time_on_page || 0) - (a.time_on_page || 0))[0] || {};
const waConsent = waPhone && (payload.whatsapp_consent === true || payload.wa_opted_in === "yes") ? "yes" : (waPhone ? "yes" : "no");
return {
  wa_phone: waPhone,
  wa_consent: waConsent,
  customer_email: email,
  customer_name: name,
  top_product_id: top.id || top.product_id || "",
  top_product_title: top.title || top.name || "an item you viewed",
  top_product_price: top.price || "",
  top_product_url: top.url || top.product_url || "",
  products_viewed: String(products.length)
};`,
          outputVariables: [
            { name: "wa_phone", type: "string" },
            { name: "wa_consent", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "top_product_id", type: "string" },
            { name: "top_product_title", type: "string" },
            { name: "top_product_price", type: "string" },
            { name: "top_product_url", type: "string" },
            { name: "products_viewed", type: "string" },
          ],
        },
      }),

      // 3. WhatsApp consent condition
      n("3", "condition", "WhatsApp consent granted?", 520, {
        conditionData: [
          {
            variable: "wa_consent",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Product detail pull (Shopify)
      shopifyNode(
        "4",
        "Pull product details (Shopify)",
        760,
        "GET",
        "products/{top_product_id}.json?fields=id,title,body_html,images,variants",
        [],
        [
          {
            name: "product.title",
            customName: "full_product_title",
            type: "string",
          },
          {
            name: "product.images[0].src",
            customName: "product_image_url",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "full_product_price",
            type: "string",
          },
          {
            name: "product.variants[0].inventory_quantity",
            customName: "stock_qty",
            type: "number",
          },
        ],
      ),

      // 5. AI conversational recovery
      aiNode(
        "5",
        "AI conversational WhatsApp recovery",
        1000,
        "You are a friendly shopping assistant writing a WhatsApp browse recovery message. Be conversational, warm, and helpful — not salesy. Reference the product they viewed, mention one compelling benefit, and include a soft call-to-action. Keep under 200 characters for WhatsApp best practices. Respond JSON: {wa_message: string, fallback_email_subject: string, fallback_email_body: string}",
        "Customer: {customer_name}. Browsed: {full_product_title} at ${full_product_price}. Stock: {stock_qty} left. Products viewed: {products_viewed}.",
        [
          { field: "wa_message", as: "wa_recovery_message", type: "string" },
          {
            field: "fallback_email_subject",
            as: "fallback_subject",
            type: "string",
          },
          { field: "fallback_email_body", as: "fallback_body", type: "string" },
        ],
      ),

      // 6. WhatsApp message
      n("6", "whatsApp", "WhatsApp: Browse recovery message", 1240, {
        whatsappDetails: {
          templateName: "browse_recovery",
          lang: "en",
          to: "{wa_phone}",
          smsBody: "{wa_recovery_message} {top_product_url}",
        },
      }),

      // 7. Conversion logging (Sheets) / Email fallback path
      sheetsNode(
        "7",
        "Log WhatsApp recovery (Sheets)",
        1480,
        "WhatsAppBrowseRecovery",
        '[["{customer_email}","{wa_phone}","{top_product_title}","{top_product_price}","{products_viewed}","{wa_consent}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // WA consent -> product pull
      e("3", "7", "output_2"), // no consent -> log + email fallback
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 559. WHATSAPP CHECKOUT REMINDER (7 nodes)
  // ===================================================================

  "WhatsApp Checkout Reminder": {
    summary:
      "Recovers abandoned checkouts via WhatsApp with AI-generated urgency messaging. A checkout abandon event pulls cart and inventory data from Shopify, checks WhatsApp consent, AI generates an urgency-driven reminder, sends a WhatsApp message, waits for recovery, and falls back to email.",
    nodes: [
      trigger("Webhook: Checkout abandoned", "webhook_app", {
        source: "Shopify",
        event: "checkouts/update",
        howItStarts:
          "A Shopify checkout is abandoned (started but not completed within 60 minutes), triggering a WhatsApp urgency reminder for consented customers",
        setup:
          "Configure Shopify webhook for abandoned checkouts. Ensure customer WhatsApp numbers are collected at checkout. Set up WhatsApp Business API via Twilio or Meta.",
        inputFields: [],
      }),

      // 2. Cart/inventory pull (Shopify)
      n("2", "runCode", "Extract checkout data and check inventory", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse checkout abandonment data
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || (payload.customer ? payload.customer.email : "") || "";
const phone = payload.phone || payload.billing_address ? (payload.billing_address.phone || "") : "";
const name = payload.customer ? (payload.customer.first_name || "") : (payload.first_name || "");
const cartItems = payload.line_items || payload.items || [];
const cartValue = parseFloat(payload.total_price || payload.subtotal_price || "0");
const recoveryUrl = payload.abandoned_checkout_url || payload.recovery_url || "";
const waConsent = payload.whatsapp_consent || (phone ? "yes" : "no");
const firstItem = cartItems[0] || {};
const lowStock = cartItems.some(i => (i.inventory_quantity || 999) < 5);
return {
  customer_email: email,
  customer_phone: phone,
  customer_name: name,
  cart_value: cartValue.toFixed(2),
  cart_items: cartItems.map(i => (i.title || i.name) + " x" + (i.quantity || 1)).join(", ") || "your items",
  cart_item_count: String(cartItems.length),
  recovery_url: recoveryUrl,
  wa_consent: waConsent,
  first_item: firstItem.title || firstItem.name || "your item",
  low_stock_alert: lowStock ? "yes" : "no"
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "cart_value", type: "string" },
            { name: "cart_items", type: "string" },
            { name: "cart_item_count", type: "string" },
            { name: "recovery_url", type: "string" },
            { name: "wa_consent", type: "string" },
            { name: "first_item", type: "string" },
            { name: "low_stock_alert", type: "string" },
          ],
        },
      }),

      // 3. WhatsApp consent condition
      n("3", "condition", "WhatsApp consent?", 520, {
        conditionData: [
          {
            variable: "wa_consent",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI urgency reminder
      aiNode(
        "4",
        "AI checkout urgency reminder",
        760,
        "You are a friendly checkout recovery assistant for WhatsApp. Write a concise, warm checkout reminder that creates gentle urgency. If low stock, mention scarcity. Reference the cart value and first item. Include a clear CTA. Keep under 200 characters for WhatsApp. Respond JSON: {wa_message: string, email_subject: string, email_body: string}",
        "Customer: {customer_name}. Cart: {cart_items} (${cart_value}). Low stock: {low_stock_alert}. Recovery URL: {recovery_url}.",
        [
          { field: "wa_message", as: "wa_checkout_msg", type: "string" },
          {
            field: "email_subject",
            as: "email_fallback_subject",
            type: "string",
          },
          { field: "email_body", as: "email_fallback_body", type: "string" },
        ],
      ),

      // 5. WhatsApp message
      n("5", "whatsApp", "WhatsApp: Checkout reminder", 1000, {
        whatsappDetails: {
          templateName: "checkout_reminder",
          lang: "en",
          to: "{customer_phone}",
          smsBody: "{wa_checkout_msg} Complete your order: {recovery_url}",
        },
      }),

      // 6. Recovery delay check
      n("6", "delayAction", "Wait for checkout completion (3 hours)", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 3, unit: "hours" },
        },
      }),

      // 7. Email fallback path
      emailNode(
        "7",
        "Email fallback: checkout reminder",
        1480,
        "{customer_email}",
        "{email_fallback_subject}",
        "Hi {customer_name},\\n\\n{email_fallback_body}\\n\\nYour cart ({cart_item_count} items, ${cart_value}):\\n{cart_items}\\n\\nComplete your checkout: {recovery_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // WA consent -> reminder
      e("3", "7", "output_2"), // no consent -> email fallback
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 560. WHATSAPP ORDER CONFIRMATION BOT (8 nodes)
  // ===================================================================

  "WhatsApp Order Confirmation Bot": {
    summary:
      "Sends order confirmations via WhatsApp with conversational reply handling. An order paid trigger assembles the order summary via runCode, checks WhatsApp template availability, AI generates a confirmation message, sends it via WhatsApp, handles reply routing, logs conversations to Sheets, and falls back to email.",
    nodes: [
      trigger("Webhook: Order paid", "webhook_app", {
        source: "Shopify",
        event: "orders/paid",
        howItStarts:
          "A Shopify order payment is confirmed, triggering a WhatsApp order confirmation with conversational reply support",
        setup:
          "Configure Shopify orders/paid webhook. Set up WhatsApp Business API with approved message templates. Ensure customer WhatsApp numbers are captured at checkout.",
        inputFields: [],
      }),

      // 2. Order summary assembly (runCode)
      n("2", "runCode", "Assemble order summary", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build structured order summary for WhatsApp
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.contact_email || (payload.customer ? payload.customer.email : "") || "";
const phone = payload.phone || payload.billing_address ? (payload.billing_address.phone || "") : (payload.customer ? (payload.customer.phone || "") : "");
const name = payload.customer ? (payload.customer.first_name || "") : "";
const orderName = payload.name || payload.order_number || "";
const items = payload.line_items || [];
const total = payload.total_price || "0.00";
const currency = payload.currency || "USD";
const shippingMethod = payload.shipping_lines ? (payload.shipping_lines[0] ? payload.shipping_lines[0].title : "Standard") : "Standard";
const itemList = items.map(i => (i.title || i.name) + " x" + (i.quantity || 1) + " — $" + (i.price || "0")).join("\\n");
const waTemplateAvailable = phone ? "yes" : "no";
return {
  customer_email: email,
  customer_phone: phone,
  customer_name: name,
  order_name: orderName,
  order_total: total,
  order_currency: currency,
  shipping_method: shippingMethod,
  item_list: itemList,
  item_count: String(items.length),
  wa_template_available: waTemplateAvailable,
  order_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_name", type: "string" },
            { name: "order_total", type: "string" },
            { name: "order_currency", type: "string" },
            { name: "shipping_method", type: "string" },
            { name: "item_list", type: "string" },
            { name: "item_count", type: "string" },
            { name: "wa_template_available", type: "string" },
            { name: "order_date", type: "string" },
          ],
        },
      }),

      // 3. WhatsApp template check condition
      n("3", "condition", "WhatsApp template available?", 520, {
        conditionData: [
          {
            variable: "wa_template_available",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI confirmation message
      aiNode(
        "4",
        "AI order confirmation message",
        760,
        "You are a friendly order confirmation assistant for WhatsApp. Write a warm, clear order confirmation that includes order number, items, total, and shipping method. Add a note that the customer can reply with questions. Keep professional but conversational. Respond JSON: {wa_confirmation: string, reply_prompt: string}",
        "Customer: {customer_name}. Order: {order_name}. Items: {item_list}. Total: {order_currency} {order_total}. Shipping: {shipping_method}.",
        [
          {
            field: "wa_confirmation",
            as: "wa_confirmation_msg",
            type: "string",
          },
          { field: "reply_prompt", as: "wa_reply_prompt", type: "string" },
        ],
      ),

      // 5. WhatsApp send
      n("5", "whatsApp", "WhatsApp: Order confirmation", 1000, {
        whatsappDetails: {
          templateName: "order_confirmation",
          lang: "en",
          to: "{customer_phone}",
          smsBody: "{wa_confirmation_msg}\\n\\n{wa_reply_prompt}",
        },
      }),

      // 6. Reply handling delay
      n("6", "delayAction", "Monitor for customer replies (1 hour)", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 1, unit: "hours" },
        },
      }),

      // 7. Conversation logging (Sheets)
      sheetsNode(
        "7",
        "Log WhatsApp conversation (Sheets)",
        1480,
        "WhatsAppOrderConversations",
        '[["{order_date}","{customer_phone}","{customer_email}","{order_name}","{order_total}","{item_count}","{shipping_method}","{wa_template_available}"]]',
      ),

      // 8. Email fallback
      emailNode(
        "8",
        "Email fallback: order confirmation",
        1720,
        "{customer_email}",
        "Order Confirmed - {order_name} (${order_total})",
        "Hi {customer_name},\\n\\nThank you for your order!\\n\\nOrder: {order_name}\\nDate: {order_date}\\n\\nItems:\\n{item_list}\\n\\nTotal: {order_currency} {order_total}\\nShipping: {shipping_method}\\n\\nWe will send you tracking information once your order ships. Reply to this email if you have any questions.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // WA available -> confirmation
      e("3", "8", "output_2"), // no WA -> email fallback
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 561. WHOLESALE ORDER FORM BOT (9 nodes)
  // ===================================================================

  "Wholesale Order Form Bot": {
    summary:
      "Processes B2B wholesale quotes end-to-end with account verification and approval. A B2B quote trigger verifies the account in Salesforce, checks account status, AI assists with order configuration, validates inventory in Shopify, creates a quote in Sheets, routes through Slack approval, creates a draft order in Shopify, and sends buyer confirmation email.",
    nodes: [
      trigger("Webhook: B2B wholesale quote request", "webhook_form", {
        source: "B2B Portal / Website",
        event: "wholesale.quote.requested",
        howItStarts:
          "A B2B buyer submits a wholesale quote request with product list, quantities, and account information, triggering automated processing and approval",
        setup:
          "Configure B2B portal to post quote requests to webhook URL. Set up Salesforce for account verification. Provide Shopify and Google Sheets API credentials.",
        inputFields: [],
      }),

      // 2. Account verification (Salesforce)
      salesforceNode(
        "2",
        "Verify B2B account (Salesforce)",
        280,
        "GET",
        "query/?q=SELECT+Id,Name,Type,BillingCity,BillingCountry,Account_Status__c,Credit_Limit__c,Payment_Terms__c+FROM+Account+WHERE+Name='{account_name}'+LIMIT+1",
        [],
        [
          {
            name: "records[0].Id",
            customName: "sf_account_id",
            type: "string",
          },
          {
            name: "records[0].Name",
            customName: "verified_account_name",
            type: "string",
          },
          {
            name: "records[0].Account_Status__c",
            customName: "account_status",
            type: "string",
          },
          {
            name: "records[0].Credit_Limit__c",
            customName: "credit_limit",
            type: "string",
          },
          {
            name: "records[0].Payment_Terms__c",
            customName: "payment_terms",
            type: "string",
          },
          {
            name: "records[0].BillingCity",
            customName: "billing_city",
            type: "string",
          },
          {
            name: "records[0].BillingCountry",
            customName: "billing_country",
            type: "string",
          },
        ],
      ),

      // 3. Account status condition
      n("3", "condition", "Account active and verified?", 520, {
        conditionData: [
          {
            variable: "account_status",
            value: "Active",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI order assistant
      aiNode(
        "4",
        "AI wholesale order assistant",
        760,
        "You are a B2B wholesale order specialist. Review the quote request, validate quantities against minimum order thresholds, suggest volume discounts, calculate estimated totals with the account's payment terms, and flag any concerns. Respond JSON: {validated_items: string, subtotal: string, volume_discount_pct: string, discount_amount: string, estimated_total: string, payment_terms_note: string, concerns: string, order_notes: string}",
        "Account: {verified_account_name} (Credit: ${credit_limit}, Terms: {payment_terms}). Requested items: {quote_items}. Quantities: {quote_quantities}. Billing: {billing_city}, {billing_country}.",
        [
          { field: "validated_items", as: "validated_items", type: "string" },
          { field: "subtotal", as: "quote_subtotal", type: "string" },
          {
            field: "volume_discount_pct",
            as: "volume_discount_pct",
            type: "string",
          },
          { field: "discount_amount", as: "discount_amount", type: "string" },
          { field: "estimated_total", as: "quote_total", type: "string" },
          {
            field: "payment_terms_note",
            as: "payment_terms_note",
            type: "string",
          },
          { field: "concerns", as: "order_concerns", type: "string" },
          { field: "order_notes", as: "order_notes", type: "string" },
        ],
      ),

      // 5. Inventory check (Shopify)
      shopifyNode(
        "5",
        "Check inventory availability (Shopify)",
        1000,
        "GET",
        "inventory_levels.json?inventory_item_ids={inventory_item_ids}&location_ids={warehouse_location_id}",
        [],
        [
          {
            name: "inventory_levels",
            customName: "inventory_data",
            type: "string",
          },
          {
            name: "inventory_levels.length",
            customName: "inventory_checked",
            type: "number",
          },
        ],
      ),

      // 6. Quote creation (Sheets)
      sheetsNode(
        "6",
        "Create wholesale quote (Sheets)",
        1240,
        "WholesaleQuotes",
        '[["{verified_account_name}","{sf_account_id}","{validated_items}","{quote_subtotal}","{volume_discount_pct}","{discount_amount}","{quote_total}","{payment_terms}","{order_concerns}","pending_approval"]]',
      ),

      // 7. Slack approval
      slackNode(
        "7",
        "Slack: Wholesale quote approval",
        1480,
        ":package: Wholesale Quote Approval Required\\nAccount: {verified_account_name} ({sf_account_id})\\nCredit Limit: ${credit_limit} | Terms: {payment_terms}\\nItems: {validated_items}\\nSubtotal: ${quote_subtotal} | Discount: {volume_discount_pct}%\\nTotal: ${quote_total}\\nConcerns: {order_concerns}\\nNotes: {order_notes}\\n\\nReact :white_check_mark: to approve, :x: to reject.",
        400,
      ),

      // 8. Draft order creation (Shopify)
      shopifyNode(
        "8",
        "Create draft order (Shopify)",
        1720,
        "POST",
        "draft_orders.json",
        [
          {
            key: "draft_order",
            value:
              '{"line_items":{validated_items},"customer":{"id":"{shopify_customer_id}"},"note":"{order_notes}","tags":"wholesale,b2b,{verified_account_name}"}',
          },
        ],
        [
          {
            name: "draft_order.id",
            customName: "draft_order_id",
            type: "string",
          },
          {
            name: "draft_order.name",
            customName: "draft_order_name",
            type: "string",
          },
          {
            name: "draft_order.invoice_url",
            customName: "invoice_url",
            type: "string",
          },
        ],
      ),

      // 9. Buyer confirmation email
      emailNode(
        "9",
        "Send wholesale quote confirmation",
        1960,
        "{buyer_email}",
        "Wholesale Quote Ready - {draft_order_name} for {verified_account_name}",
        "Dear {buyer_name},\\n\\nYour wholesale quote has been processed and approved.\\n\\nAccount: {verified_account_name}\\nQuote Total: ${quote_total} (includes {volume_discount_pct}% volume discount)\\nPayment Terms: {payment_terms}\\n{payment_terms_note}\\n\\nItems: {validated_items}\\n\\nDraft Order: {draft_order_name}\\nInvoice: {invoice_url}\\n\\nNotes: {order_notes}\\n\\nTo finalize this order, please review and approve the invoice link above. If you have any questions, reply to this email.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // active account -> AI assistant
      e("3", "9", "output_2"), // inactive -> notify buyer of issue
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 562. WISHLIST REMINDER (8 nodes)
  // ===================================================================

  "Wishlist Reminder": {
    summary:
      "Sends intelligent wishlist reminders based on price drops, low stock, and back-in-stock events. A daily scan detects changes via runCode, determines trigger type, AI personalizes the reminder, sends an email, sends SMS for critical alerts, logs trigger-to-purchase data in Sheets, and optimizes cadence.",
    nodes: [
      trigger("Schedule: Daily wishlist change scan", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9 AM to scan all wishlist items for price drops, low stock alerts, and back-in-stock events, then sends personalized reminders",
        setup:
          "Configure wishlist data source (Shopify metafields, dedicated wishlist app, or database). Set up Twilio for SMS alerts on critical changes (back-in-stock, deep discounts).",
        inputFields: [],
      }),

      // 2. Price/stock change detection (runCode)
      n("2", "runCode", "Detect price and stock changes", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Scan wishlist items for actionable changes
const wishlistData = JSON.parse(vars.wishlist_snapshot || "[]");
const triggers = [];
for (const item of wishlistData) {
  const priceDropPct = item.original_price > 0 ? ((item.original_price - item.current_price) / item.original_price * 100) : 0;
  const isPriceDrop = priceDropPct >= 10;
  const isLowStock = item.inventory_quantity > 0 && item.inventory_quantity <= 5;
  const isBackInStock = item.was_out_of_stock && item.inventory_quantity > 0;
  if (isPriceDrop || isLowStock || isBackInStock) {
    triggers.push({
      product_id: item.product_id,
      title: item.title,
      original_price: item.original_price,
      current_price: item.current_price,
      price_drop_pct: priceDropPct.toFixed(0),
      stock: item.inventory_quantity,
      trigger_type: isBackInStock ? "back_in_stock" : isPriceDrop ? "price_drop" : "low_stock",
      url: item.product_url,
      customer_email: item.customer_email,
      customer_phone: item.customer_phone || "",
      customer_name: item.customer_name || ""
    });
  }
}
const topTrigger = triggers[0] || {};
const triggerType = topTrigger.trigger_type || "none";
const isCritical = triggerType === "back_in_stock" || (triggerType === "price_drop" && parseFloat(topTrigger.price_drop_pct || "0") >= 25) ? "yes" : "no";
return {
  total_triggers: String(triggers.length),
  trigger_type: triggerType,
  is_critical: isCritical,
  customer_email: topTrigger.customer_email || "",
  customer_phone: topTrigger.customer_phone || "",
  customer_name: topTrigger.customer_name || "",
  product_title: topTrigger.title || "",
  original_price: String(topTrigger.original_price || ""),
  current_price: String(topTrigger.current_price || ""),
  price_drop_pct: topTrigger.price_drop_pct || "0",
  stock_remaining: String(topTrigger.stock || ""),
  product_url: topTrigger.url || "",
  triggers_json: JSON.stringify(triggers.slice(0, 10))
};`,
          outputVariables: [
            { name: "total_triggers", type: "string" },
            { name: "trigger_type", type: "string" },
            { name: "is_critical", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "product_title", type: "string" },
            { name: "original_price", type: "string" },
            { name: "current_price", type: "string" },
            { name: "price_drop_pct", type: "string" },
            { name: "stock_remaining", type: "string" },
            { name: "product_url", type: "string" },
            { name: "triggers_json", type: "string" },
          ],
        },
      }),

      // 3. Trigger type condition
      n("3", "condition", "Actionable trigger found?", 520, {
        conditionData: [
          {
            variable: "trigger_type",
            value: "price_drop",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "trigger_type",
            value: "low_stock",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "trigger_type",
            value: "back_in_stock",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 4. AI personalized reminder
      aiNode(
        "4",
        "AI personalized wishlist reminder",
        760,
        "You are a personal shopping assistant. Write a personalized wishlist reminder based on the trigger type. For price drops, emphasize savings. For low stock, create urgency. For back-in-stock, convey excitement. Reference the specific product and price change. Respond JSON: {email_subject: string, email_body: string, sms_text: string, urgency_level: string}",
        "Trigger: {trigger_type}. Product: {product_title}. Original: ${original_price}, Now: ${current_price} ({price_drop_pct}% off). Stock: {stock_remaining} left. Customer: {customer_name}. URL: {product_url}.",
        [
          { field: "email_subject", as: "reminder_subject", type: "string" },
          { field: "email_body", as: "reminder_body", type: "string" },
          { field: "sms_text", as: "reminder_sms", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 5. Email reminder
      emailNode(
        "5",
        "Send wishlist reminder email",
        1000,
        "{customer_email}",
        "{reminder_subject}",
        "Hi {customer_name},\\n\\n{reminder_body}\\n\\n{product_title}\\nWas: ${original_price} -> Now: ${current_price}\\nStock: {stock_remaining} remaining\\n\\nShop now: {product_url}",
      ),

      // 6. SMS for critical triggers (Twilio)
      smsNode(
        "6",
        "SMS: Critical wishlist alert (Twilio)",
        1240,
        "{customer_phone}",
        "{reminder_sms} Shop now: {product_url}",
      ),

      // 7. Trigger-to-purchase logging (Sheets)
      sheetsNode(
        "7",
        "Log wishlist trigger data (Sheets)",
        1480,
        "WishlistTriggers",
        '[["{customer_email}","{trigger_type}","{product_title}","{original_price}","{current_price}","{price_drop_pct}","{stock_remaining}","{is_critical}","{urgency_level}"]]',
      ),

      // 8. Cadence optimization
      n("8", "runCode", "Optimize reminder cadence", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track reminder frequency to avoid over-messaging
const email = vars.customer_email || "";
const triggerType = vars.trigger_type || "";
const now = new Date().toISOString();
return {
  cadence_key: email + "_wishlist_" + triggerType,
  last_reminder_sent: now,
  cooldown_hours: triggerType === "back_in_stock" ? "0" : triggerType === "price_drop" ? "72" : "168",
  next_eligible: triggerType === "back_in_stock" ? now : new Date(Date.now() + (triggerType === "price_drop" ? 72 : 168) * 3600000).toISOString()
};`,
          outputVariables: [
            { name: "cadence_key", type: "string" },
            { name: "last_reminder_sent", type: "string" },
            { name: "cooldown_hours", type: "string" },
            { name: "next_eligible", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // actionable trigger -> personalize
      e("3", "8", "output_2"), // no trigger -> cadence update only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 563. WOOCOMMERCE ORDER TO SHIPSTATION (7 nodes)
  // ===================================================================

  "WooCommerce Order to ShipStation": {
    summary:
      "Syncs WooCommerce orders to ShipStation with carrier mapping and address validation. A WooCommerce order webhook extracts order and shipping data, maps carriers via runCode, validates the shipping address, creates the ShipStation order, syncs tracking back, and alerts ops on Slack for failures.",
    nodes: [
      trigger("Webhook: WooCommerce order created", "webhook_app", {
        source: "WooCommerce",
        event: "order.created",
        howItStarts:
          "A WooCommerce order is placed and paid, triggering automatic fulfillment setup in ShipStation with carrier selection and address validation",
        setup:
          "Configure WooCommerce webhook for order.created events. Set up ShipStation API credentials. Map WooCommerce shipping methods to ShipStation carriers.",
        inputFields: [],
      }),

      // 2. Order/shipping data pull
      n("2", "runCode", "Extract WooCommerce order and shipping data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse WooCommerce order payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const order = payload;
const shipping = order.shipping || {};
const billing = order.billing || {};
const items = (order.line_items || []).map(i => ({
  sku: i.sku || "",
  name: i.name || "",
  quantity: i.quantity || 1,
  weight: i.weight || "0"
}));
const shippingMethod = (order.shipping_lines || [])[0] ? order.shipping_lines[0].method_title : "Standard";
const totalWeight = items.reduce((s, i) => s + parseFloat(i.weight || "0") * i.quantity, 0);
return {
  wc_order_id: String(order.id || order.number || ""),
  wc_order_number: order.number || String(order.id || ""),
  order_total: order.total || "0.00",
  customer_email: billing.email || order.email || "",
  customer_name: (shipping.first_name || billing.first_name || "") + " " + (shipping.last_name || billing.last_name || ""),
  ship_address_1: shipping.address_1 || "",
  ship_address_2: shipping.address_2 || "",
  ship_city: shipping.city || "",
  ship_state: shipping.state || "",
  ship_postcode: shipping.postcode || "",
  ship_country: shipping.country || "",
  shipping_method: shippingMethod,
  items_json: JSON.stringify(items),
  item_count: String(items.length),
  total_weight_oz: String((totalWeight * 16).toFixed(1)),
  address_complete: (shipping.address_1 && shipping.city && shipping.postcode && shipping.country) ? "yes" : "no"
};`,
          outputVariables: [
            { name: "wc_order_id", type: "string" },
            { name: "wc_order_number", type: "string" },
            { name: "order_total", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "ship_address_1", type: "string" },
            { name: "ship_address_2", type: "string" },
            { name: "ship_city", type: "string" },
            { name: "ship_state", type: "string" },
            { name: "ship_postcode", type: "string" },
            { name: "ship_country", type: "string" },
            { name: "shipping_method", type: "string" },
            { name: "items_json", type: "string" },
            { name: "item_count", type: "string" },
            { name: "total_weight_oz", type: "string" },
            { name: "address_complete", type: "string" },
          ],
        },
      }),

      // 3. Carrier mapping (runCode)
      n("3", "runCode", "Map shipping method to carrier", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map WooCommerce shipping methods to ShipStation carrier codes
const method = (vars.shipping_method || "").toLowerCase();
const country = vars.ship_country || "US";
let carrierCode = "stamps_com";
let serviceCode = "usps_priority_mail";
if (method.includes("express") || method.includes("overnight") || method.includes("fedex")) {
  carrierCode = "fedex";
  serviceCode = method.includes("overnight") ? "fedex_standard_overnight" : "fedex_2day";
} else if (method.includes("ups") || method.includes("ground")) {
  carrierCode = "ups";
  serviceCode = method.includes("2day") || method.includes("2nd") ? "ups_2nd_day_air" : "ups_ground";
} else if (country !== "US") {
  carrierCode = "stamps_com";
  serviceCode = "usps_priority_mail_international";
} else if (method.includes("free") || method.includes("standard")) {
  carrierCode = "stamps_com";
  serviceCode = "usps_first_class_mail";
}
return {
  carrier_code: carrierCode,
  service_code: serviceCode,
  carrier_display: carrierCode.replace(/_/g, " ").toUpperCase()
};`,
          outputVariables: [
            { name: "carrier_code", type: "string" },
            { name: "service_code", type: "string" },
            { name: "carrier_display", type: "string" },
          ],
        },
      }),

      // 4. Address validation condition
      n("4", "condition", "Shipping address complete?", 760, {
        conditionData: [
          {
            variable: "address_complete",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. ShipStation order creation
      n("5", "hitApi", "Create ShipStation order", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://ssapi.shipstation.com/orders/createorder",
          headers: [
            { key: "Authorization", value: "Basic {shipstation_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "orderNumber", value: "{wc_order_number}" },
            { key: "orderDate", value: "{current_date}" },
            { key: "orderStatus", value: "awaiting_shipment" },
            { key: "customerEmail", value: "{customer_email}" },
            {
              key: "shipTo",
              value:
                '{"name":"{customer_name}","street1":"{ship_address_1}","street2":"{ship_address_2}","city":"{ship_city}","state":"{ship_state}","postalCode":"{ship_postcode}","country":"{ship_country}"}',
            },
            { key: "carrierCode", value: "{carrier_code}" },
            { key: "serviceCode", value: "{service_code}" },
            {
              key: "weight",
              value: '{"value":{total_weight_oz},"units":"ounces"}',
            },
            { key: "items", value: "{items_json}" },
          ],
          variables: [
            { name: "orderId", customName: "ss_order_id", type: "string" },
            {
              name: "orderNumber",
              customName: "ss_order_number",
              type: "string",
            },
            { name: "orderStatus", customName: "ss_status", type: "string" },
          ],
        },
      }),

      // 6. Tracking sync back (WooCommerce note)
      n("6", "hitApi", "Sync ShipStation ID to WooCommerce", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wc_store_url}/wp-json/wc/v3/orders/{wc_order_id}/notes",
          headers: [
            { key: "Authorization", value: "Basic {wc_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "note",
              value:
                "ShipStation order created: #{ss_order_id}. Carrier: {carrier_display} ({service_code}). Status: {ss_status}.",
            },
          ],
          variables: [{ name: "id", customName: "wc_note_id", type: "string" }],
        },
      }),

      // 7. Ops failure Slack alert
      slackNode(
        "7",
        "Slack: ShipStation sync alert",
        1480,
        ":truck: WooCommerce -> ShipStation Sync\\nWC Order: #{wc_order_number} -> SS Order: #{ss_order_id}\\nCarrier: {carrier_display} ({service_code})\\nWeight: {total_weight_oz}oz | Items: {item_count}\\nShip to: {customer_name}, {ship_city}, {ship_state} {ship_postcode}, {ship_country}\\nStatus: {ss_status}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // address complete -> create SS order
      e("4", "7", "output_2"), // address incomplete -> Slack alert only
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 564. WOOCOMMERCE SHOPIFY PRODUCT SYNC (8 nodes)
  // ===================================================================

  "WooCommerce Shopify Product Sync": {
    summary:
      "Bi-directional product sync between WooCommerce and Shopify with conflict resolution. A product change webhook detects the source of truth, normalizes data via runCode, calculates inventory deltas, pushes to WooCommerce, pushes to Shopify, audits the sync in Sheets, and alerts on Slack for mismatches.",
    nodes: [
      trigger("Webhook: Product data changed", "webhook_app", {
        source: "WooCommerce / Shopify",
        event: "product.updated",
        howItStarts:
          "A product title, price, inventory, or description changes in either WooCommerce or Shopify, triggering bi-directional sync with conflict resolution",
        setup:
          "Configure webhooks on both WooCommerce (product.updated) and Shopify (products/update). Set source-of-truth rules (e.g., Shopify is master for price, WooCommerce for descriptions). Provide API credentials for both platforms.",
        inputFields: [],
      }),

      // 2. Source-of-truth detection (runCode)
      n("2", "runCode", "Detect source platform and extract data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine which platform triggered and extract normalized product data
const payload = JSON.parse(vars.webhook_payload || "{}");
let source = "unknown";
let productId = "";
let title = "";
let price = "";
let sku = "";
let inventory = 0;
let description = "";

// Shopify format detection
if (payload.admin_graphql_api_id || payload.handle) {
  source = "shopify";
  productId = String(payload.id || "");
  title = payload.title || "";
  price = payload.variants ? (payload.variants[0] ? payload.variants[0].price : "") : "";
  sku = payload.variants ? (payload.variants[0] ? payload.variants[0].sku : "") : "";
  inventory = payload.variants ? (payload.variants[0] ? (payload.variants[0].inventory_quantity || 0) : 0) : 0;
  description = payload.body_html || "";
}
// WooCommerce format detection
else if (payload.permalink || payload.slug) {
  source = "woocommerce";
  productId = String(payload.id || "");
  title = payload.name || "";
  price = payload.price || payload.regular_price || "";
  sku = payload.sku || "";
  inventory = parseInt(payload.stock_quantity || "0", 10);
  description = payload.description || "";
}

return {
  source_platform: source,
  source_product_id: productId,
  product_title: title,
  product_price: price,
  product_sku: sku,
  product_inventory: String(inventory),
  product_description: description,
  sync_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "source_platform", type: "string" },
            { name: "source_product_id", type: "string" },
            { name: "product_title", type: "string" },
            { name: "product_price", type: "string" },
            { name: "product_sku", type: "string" },
            { name: "product_inventory", type: "string" },
            { name: "product_description", type: "string" },
            { name: "sync_timestamp", type: "string" },
          ],
        },
      }),

      // 3. Data normalization
      n("3", "runCode", "Normalize and detect deltas", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize product data for cross-platform sync
const source = vars.source_platform || "unknown";
const title = (vars.product_title || "").trim();
const price = parseFloat(vars.product_price || "0").toFixed(2);
const inventory = parseInt(vars.product_inventory || "0", 10);
const sku = (vars.product_sku || "").trim().toUpperCase();
const description = (vars.product_description || "").replace(/<[^>]+>/g, "").trim().substring(0, 500);
return {
  normalized_title: title,
  normalized_price: price,
  normalized_sku: sku,
  normalized_inventory: String(inventory),
  normalized_description: description,
  target_platform: source === "shopify" ? "woocommerce" : "shopify",
  sync_direction: source + "_to_" + (source === "shopify" ? "woocommerce" : "shopify")
};`,
          outputVariables: [
            { name: "normalized_title", type: "string" },
            { name: "normalized_price", type: "string" },
            { name: "normalized_sku", type: "string" },
            { name: "normalized_inventory", type: "string" },
            { name: "normalized_description", type: "string" },
            { name: "target_platform", type: "string" },
            { name: "sync_direction", type: "string" },
          ],
        },
      }),

      // 4. Inventory delta calc
      n("4", "runCode", "Calculate inventory delta", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate inventory adjustment needed
const sourceInventory = parseInt(vars.normalized_inventory || "0", 10);
const delta = sourceInventory;  // Will be set as absolute on target
return {
  inventory_delta: String(delta),
  inventory_action: delta > 0 ? "set" : "set_zero",
  needs_inventory_sync: "yes"
};`,
          outputVariables: [
            { name: "inventory_delta", type: "string" },
            { name: "inventory_action", type: "string" },
            { name: "needs_inventory_sync", type: "string" },
          ],
        },
      }),

      // 5. WooCommerce push
      n("5", "hitApi", "Push product to WooCommerce", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{wc_store_url}/wp-json/wc/v3/products/{wc_product_id}",
          headers: [
            { key: "Authorization", value: "Basic {wc_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{normalized_title}" },
            { key: "regular_price", value: "{normalized_price}" },
            { key: "stock_quantity", value: "{normalized_inventory}" },
            { key: "description", value: "{normalized_description}" },
          ],
          variables: [
            { name: "id", customName: "wc_synced_id", type: "string" },
            {
              name: "date_modified",
              customName: "wc_modified",
              type: "string",
            },
          ],
        },
      }),

      // 6. Shopify push
      shopifyNode(
        "6",
        "Push product to Shopify",
        1240,
        "PUT",
        "products/{shopify_product_id}.json",
        [
          {
            key: "product",
            value:
              '{"title":"{normalized_title}","body_html":"{normalized_description}","variants":[{"price":"{normalized_price}","sku":"{normalized_sku}","inventory_quantity":{normalized_inventory}}]}',
          },
        ],
        [
          {
            name: "product.id",
            customName: "shopify_synced_id",
            type: "string",
          },
          {
            name: "product.updated_at",
            customName: "shopify_modified",
            type: "string",
          },
        ],
      ),

      // 7. Sync audit (Sheets)
      sheetsNode(
        "7",
        "Log product sync audit (Sheets)",
        1480,
        "ProductSyncAudit",
        '[["{sync_timestamp}","{sync_direction}","{normalized_sku}","{normalized_title}","{normalized_price}","{normalized_inventory}","{source_product_id}","{wc_synced_id}","{shopify_synced_id}"]]',
      ),

      // 8. Mismatch Slack alert
      slackNode(
        "8",
        "Slack: Product sync status",
        1720,
        ":arrows_counterclockwise: Product Sync Complete\\nDirection: {sync_direction}\\nSKU: {normalized_sku} | {normalized_title}\\nPrice: ${normalized_price} | Inventory: {normalized_inventory}\\nWC ID: {wc_synced_id} | Shopify ID: {shopify_synced_id}\\nTimestamp: {sync_timestamp}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 565. EBAY INVENTORY LISTING SYNC (7 nodes)
  // ===================================================================

  "eBay Inventory Listing Sync": {
    summary:
      "Keeps eBay inventory quantities in sync with your source system. An inventory change webhook loads listing mappings, calculates sellable quantities via runCode, updates eBay inventory, checks for zero-stock items, handles out-of-stock listing status, and logs reconciliation data to Sheets.",
    nodes: [
      trigger("Webhook: Inventory quantity changed", "webhook_app", {
        source: "Shopify / ERP",
        event: "inventory.quantity.changed",
        howItStarts:
          "An inventory level changes in your source system (Shopify, ERP, warehouse), triggering an eBay listing quantity sync to prevent overselling",
        setup:
          "Configure inventory change webhook from your source system. Set up eBay REST API credentials (OAuth 2.0). Maintain a SKU-to-eBay listing ID mapping in Google Sheets or database.",
        inputFields: [],
      }),

      // 2. Listing map load (runCode)
      n("2", "runCode", "Load listing map and extract inventory data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract inventory change data from webhook
const payload = JSON.parse(vars.webhook_payload || "{}");
const sku = payload.sku || payload.inventory_item_id || payload.product_sku || "";
const newQuantity = parseInt(payload.available || payload.quantity || payload.stock_quantity || "0", 10);
const previousQuantity = parseInt(payload.previous_quantity || payload.old_available || "0", 10);
const locationId = payload.location_id || payload.warehouse_id || "";
const productTitle = payload.product_title || payload.title || "";

// Reserve buffer for multi-channel selling
const reserveBuffer = 2;
const sellableQty = Math.max(0, newQuantity - reserveBuffer);

return {
  product_sku: sku,
  product_title: productTitle,
  source_quantity: String(newQuantity),
  previous_quantity: String(previousQuantity),
  sellable_quantity: String(sellableQty),
  location_id: locationId,
  is_zero_stock: sellableQty === 0 ? "yes" : "no",
  change_direction: newQuantity > previousQuantity ? "increase" : newQuantity < previousQuantity ? "decrease" : "none",
  change_amount: String(Math.abs(newQuantity - previousQuantity))
};`,
          outputVariables: [
            { name: "product_sku", type: "string" },
            { name: "product_title", type: "string" },
            { name: "source_quantity", type: "string" },
            { name: "previous_quantity", type: "string" },
            { name: "sellable_quantity", type: "string" },
            { name: "location_id", type: "string" },
            { name: "is_zero_stock", type: "string" },
            { name: "change_direction", type: "string" },
            { name: "change_amount", type: "string" },
          ],
        },
      }),

      // 3. Sellable quantity calc (already done in step 2, this resolves eBay listing ID)
      n("3", "hitApi", "Resolve eBay listing ID", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{mapping_sheet_id}/values/EbayMappings!A:C",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "sku_mapping_data", type: "string" },
          ],
        },
      }),

      // 4. eBay inventory update
      n("4", "hitApi", "Update eBay inventory quantity", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.ebay.com/sell/inventory/v1/inventory_item/{product_sku}",
          headers: [
            { key: "Authorization", value: "Bearer {ebay_oauth_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Content-Language", value: "en-US" },
          ],
          body: [
            {
              key: "availability",
              value:
                '{"shipToLocationAvailability":{"quantity":{sellable_quantity}}}',
            },
          ],
          variables: [
            { name: "sku", customName: "ebay_sku_updated", type: "string" },
          ],
        },
      }),

      // 5. Zero-stock condition
      n("5", "condition", "Zero stock remaining?", 1000, {
        conditionData: [
          {
            variable: "is_zero_stock",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Out-of-stock handling
      n("6", "hitApi", "Set eBay listing to out-of-stock", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.ebay.com/sell/inventory/v1/offer/{ebay_offer_id}/withdraw",
          headers: [
            { key: "Authorization", value: "Bearer {ebay_oauth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "listingId",
              customName: "withdrawn_listing_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Reconciliation logging (Sheets)
      sheetsNode(
        "7",
        "Log inventory reconciliation (Sheets)",
        1480,
        "EbayInventorySync",
        '[["{product_sku}","{product_title}","{previous_quantity}","{source_quantity}","{sellable_quantity}","{is_zero_stock}","{change_direction}","{change_amount}","{ebay_sku_updated}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // zero stock -> withdraw listing
      e("5", "7", "output_2"), // has stock -> log only
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 566. EBAY MULTI-VARIATION LISTING (8 nodes)
  // ===================================================================

  "eBay Multi-Variation Listing": {
    summary:
      "Creates eBay multi-variation listings from Shopify product data. A multi-variant product trigger pulls variant and attribute data from Shopify, resolves eBay category specifics via runCode, AI generates optimized titles and descriptions, builds the parent-child variation model, creates the eBay listing, stores ID mappings in Sheets, and sends a Slack verification.",
    nodes: [
      trigger("Webhook: Multi-variant product ready for eBay", "webhook_app", {
        source: "Shopify / Product Management",
        event: "product.ready_for_ebay",
        howItStarts:
          "A multi-variant product is approved for eBay listing, triggering automated variation listing creation with AI-optimized titles and category mapping",
        setup:
          "Configure product management system to fire webhook when a multi-variant product is approved for eBay. Set up Shopify and eBay API credentials. Maintain category mapping reference.",
        inputFields: [],
      }),

      // 2. Variant/attribute pull (Shopify)
      shopifyNode(
        "2",
        "Pull variant and attribute data (Shopify)",
        280,
        "GET",
        "products/{shopify_product_id}.json?fields=id,title,body_html,product_type,vendor,tags,options,variants,images",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.body_html",
            customName: "product_description",
            type: "string",
          },
          {
            name: "product.product_type",
            customName: "product_type",
            type: "string",
          },
          { name: "product.vendor", customName: "vendor", type: "string" },
          { name: "product.tags", customName: "product_tags", type: "string" },
          {
            name: "product.options",
            customName: "variant_options",
            type: "string",
          },
          {
            name: "product.variants",
            customName: "variants_data",
            type: "string",
          },
          {
            name: "product.images",
            customName: "product_images",
            type: "string",
          },
        ],
      ),

      // 3. Category/specifics resolution (runCode)
      n("3", "runCode", "Resolve eBay category and item specifics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map product type to eBay category and build item specifics
const productType = (vars.product_type || "").toLowerCase();
const vendor = vars.vendor || "";
const tags = vars.product_tags || "";
const options = JSON.parse(vars.variant_options || "[]");
const variants = JSON.parse(vars.variants_data || "[]");

// Category mapping (simplified — production would use eBay taxonomy API)
const categoryMap = {
  "clothing": "15687", "shoes": "93427", "electronics": "293",
  "jewelry": "281", "home": "11700", "toys": "220", "sports": "888",
  "beauty": "26395", "automotive": "6000", "books": "261186"
};
const matchedCat = Object.keys(categoryMap).find(k => productType.includes(k)) || "other";
const categoryId = categoryMap[matchedCat] || "99";

// Build variation specifics
const variationAxes = options.map(o => o.name || o);
const variantCount = variants.length;
const priceRange = variants.length > 0 ?
  Math.min(...variants.map(v => parseFloat(v.price || 0))).toFixed(2) + " - " +
  Math.max(...variants.map(v => parseFloat(v.price || 0))).toFixed(2) : "0.00";

return {
  ebay_category_id: categoryId,
  category_name: matchedCat,
  variation_axes: JSON.stringify(variationAxes),
  variant_count: String(variantCount),
  price_range: priceRange,
  brand: vendor,
  has_images: (vars.product_images || "[]") !== "[]" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "ebay_category_id", type: "string" },
            { name: "category_name", type: "string" },
            { name: "variation_axes", type: "string" },
            { name: "variant_count", type: "string" },
            { name: "price_range", type: "string" },
            { name: "brand", type: "string" },
            { name: "has_images", type: "string" },
          ],
        },
      }),

      // 4. AI title + description
      aiNode(
        "4",
        "AI optimized eBay title and description",
        760,
        "You are an eBay listing optimization specialist. Write an SEO-optimized eBay title (max 80 characters) and HTML description for a multi-variation product. Include key search terms, brand, product type, and variation highlights. The title must include the brand and most-searched attributes. Respond JSON: {ebay_title: string, ebay_description: string, search_keywords: string, listing_subtitle: string}",
        "Product: {product_title}. Type: {product_type}. Brand: {brand}. Variations: {variation_axes} ({variant_count} variants). Price range: ${price_range}. Tags: {product_tags}. Original description: {product_description}.",
        [
          { field: "ebay_title", as: "ebay_title", type: "string" },
          { field: "ebay_description", as: "ebay_description", type: "string" },
          { field: "search_keywords", as: "search_keywords", type: "string" },
          { field: "listing_subtitle", as: "listing_subtitle", type: "string" },
        ],
      ),

      // 5. Parent-child model build
      n("5", "runCode", "Build eBay variation model", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build eBay-compatible variation listing structure
const variants = JSON.parse(vars.variants_data || "[]");
const options = JSON.parse(vars.variant_options || "[]");
const images = JSON.parse(vars.product_images || "[]");

const variations = variants.map(v => {
  const specifics = {};
  (v.option1 && options[0]) && (specifics[options[0].name || options[0]] = v.option1);
  (v.option2 && options[1]) && (specifics[options[1].name || options[1]] = v.option2);
  (v.option3 && options[2]) && (specifics[options[2].name || options[2]] = v.option3);
  return {
    sku: v.sku || String(v.id),
    price: v.price || "0.00",
    quantity: v.inventory_quantity || 0,
    specifics: specifics
  };
});

return {
  variation_model: JSON.stringify(variations),
  primary_image_url: images[0] ? (images[0].src || images[0]) : "",
  image_count: String(images.length),
  total_inventory: String(variations.reduce((s, v) => s + (v.quantity || 0), 0))
};`,
          outputVariables: [
            { name: "variation_model", type: "string" },
            { name: "primary_image_url", type: "string" },
            { name: "image_count", type: "string" },
            { name: "total_inventory", type: "string" },
          ],
        },
      }),

      // 6. eBay listing creation
      n("6", "hitApi", "Create eBay multi-variation listing", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.ebay.com/sell/inventory/v1/bulk_create_or_replace_inventory_item",
          headers: [
            { key: "Authorization", value: "Bearer {ebay_oauth_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Content-Language", value: "en-US" },
          ],
          body: [{ key: "requests", value: "{variation_model}" }],
          variables: [
            {
              name: "responses",
              customName: "ebay_creation_responses",
              type: "string",
            },
          ],
        },
      }),

      // 7. ID mapping storage (Sheets)
      sheetsNode(
        "7",
        "Store eBay listing ID mappings (Sheets)",
        1480,
        "EbayListingMappings",
        '[["{product_title}","{ebay_category_id}","{category_name}","{variant_count}","{price_range}","{total_inventory}","{ebay_title}","{search_keywords}","{shopify_product_id}"]]',
      ),

      // 8. Slack verification
      slackNode(
        "8",
        "Slack: eBay listing created",
        1720,
        ":shopping_bags: eBay Multi-Variation Listing Created\\nTitle: {ebay_title}\\nCategory: {category_name} ({ebay_category_id})\\nVariants: {variant_count} | Price: ${price_range}\\nTotal Inventory: {total_inventory} | Images: {image_count}\\nShopify ID: {shopify_product_id}\\nKeywords: {search_keywords}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 567. EBAY PRICE & QUANTITY UPDATE (7 nodes)
  // ===================================================================

  "eBay Price & Quantity Update": {
    summary:
      "Scheduled delta sync for eBay prices and quantities with margin guardrails. A scheduled scan pulls source prices and quantities, calculates deltas via runCode, applies margin guardrail checks, performs eBay bulk updates, generates a results report in Sheets, and alerts on Slack for critical failures.",
    nodes: [
      trigger("Schedule: eBay price and quantity delta scan", "schedule", {
        schedule: "*/30 * * * *",
        howItStarts:
          "Runs every 30 minutes to scan for price and quantity changes, validate margins, and push updates to eBay in bulk to keep listings competitive and in-stock",
        setup:
          "Configure source system API (Shopify, ERP, or pricing engine). Set minimum margin thresholds. Provide eBay OAuth token and inventory API credentials.",
        inputFields: [],
      }),

      // 2. Source price/qty pull
      shopifyNode(
        "2",
        "Pull current prices and quantities (Shopify)",
        280,
        "GET",
        "products.json?fields=id,title,variants&limit=250",
        [],
        [
          { name: "products", customName: "source_products", type: "string" },
          {
            name: "products.length",
            customName: "source_product_count",
            type: "number",
          },
        ],
      ),

      // 3. Delta calculation (runCode)
      n("3", "runCode", "Calculate price and quantity deltas", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare source vs eBay current state and compute deltas
const products = JSON.parse(vars.source_products || "[]");
const ebayState = JSON.parse(vars.ebay_current_state || "{}");
const updates = [];
const skipped = [];
const failures = [];
let totalPriceChanges = 0;
let totalQtyChanges = 0;

for (const p of products) {
  for (const v of (p.variants || [])) {
    const sku = v.sku || "";
    if (!sku) continue;
    const sourcePrice = parseFloat(v.price || "0");
    const sourceQty = v.inventory_quantity || 0;
    const ebayItem = ebayState[sku] || {};
    const ebayPrice = parseFloat(ebayItem.price || "0");
    const ebayQty = ebayItem.quantity || 0;
    const priceDiff = Math.abs(sourcePrice - ebayPrice);
    const qtyDiff = Math.abs(sourceQty - ebayQty);
    const costPrice = parseFloat(v.compare_at_price || v.cost || "0") || sourcePrice * 0.5;
    const margin = costPrice > 0 ? ((sourcePrice - costPrice) / sourcePrice * 100) : 50;

    if (priceDiff >= 0.01 || qtyDiff >= 1) {
      updates.push({
        sku, title: p.title,
        new_price: sourcePrice.toFixed(2),
        new_qty: Math.max(0, sourceQty - 2),
        old_price: ebayPrice.toFixed(2),
        old_qty: ebayQty,
        margin: margin.toFixed(1),
        margin_safe: margin >= 15 ? "yes" : "no"
      });
      if (priceDiff >= 0.01) totalPriceChanges++;
      if (qtyDiff >= 1) totalQtyChanges++;
    }
  }
}

const safeUpdates = updates.filter(u => u.margin_safe === "yes");
const unsafeUpdates = updates.filter(u => u.margin_safe === "no");

return {
  total_updates: String(updates.length),
  price_changes: String(totalPriceChanges),
  qty_changes: String(totalQtyChanges),
  safe_updates: JSON.stringify(safeUpdates.slice(0, 50)),
  unsafe_updates: JSON.stringify(unsafeUpdates.slice(0, 20)),
  safe_count: String(safeUpdates.length),
  unsafe_count: String(unsafeUpdates.length),
  has_unsafe: unsafeUpdates.length > 0 ? "yes" : "no",
  delta_summary: safeUpdates.length + " safe, " + unsafeUpdates.length + " margin-flagged out of " + updates.length + " total"
};`,
          outputVariables: [
            { name: "total_updates", type: "string" },
            { name: "price_changes", type: "string" },
            { name: "qty_changes", type: "string" },
            { name: "safe_updates", type: "string" },
            { name: "unsafe_updates", type: "string" },
            { name: "safe_count", type: "string" },
            { name: "unsafe_count", type: "string" },
            { name: "has_unsafe", type: "string" },
            { name: "delta_summary", type: "string" },
          ],
        },
      }),

      // 4. Margin guardrail condition
      n("4", "condition", "Any margin-unsafe updates?", 760, {
        conditionData: [
          {
            variable: "has_unsafe",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. eBay bulk update
      n("5", "hitApi", "eBay bulk price and quantity update", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.ebay.com/sell/inventory/v1/bulk_update_price_quantity",
          headers: [
            { key: "Authorization", value: "Bearer {ebay_oauth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "requests", value: "{safe_updates}" }],
          variables: [
            {
              name: "responses",
              customName: "ebay_update_responses",
              type: "string",
            },
          ],
        },
      }),

      // 6. Results report (Sheets)
      sheetsNode(
        "6",
        "Log eBay sync results (Sheets)",
        1240,
        "EbayPriceQtySync",
        '[["{delta_summary}","{total_updates}","{price_changes}","{qty_changes}","{safe_count}","{unsafe_count}","{has_unsafe}"]]',
      ),

      // 7. Critical failure Slack alert
      slackNode(
        "7",
        "Slack: eBay sync report",
        1480,
        ":chart_with_upwards_trend: eBay Price & Qty Sync Complete\\n{delta_summary}\\nPrice Changes: {price_changes} | Qty Changes: {qty_changes}\\nSafe Updates Applied: {safe_count}\\nMargin-Flagged (Held): {unsafe_count}\\nUnsafe items flagged: {has_unsafe} — review margin-flagged items in Sheets before approving.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7", "output_1"), // has unsafe -> Slack alert with warning
      e("4", "5", "output_2"), // all safe -> bulk update
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
